import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import {
  analyzeErrorMessage,
  assertUploadForPath,
  uploadPathForFilename,
  userFacingFetchError,
  type UploadPath,
} from "../lib/trainAnalyzeUpload";
import {
  formatPhoneInput,
  validateCustomerForm,
} from "../utils/formValidation";
import { redirectToPayPalApprove } from "../vendors/payment/paypalCheckout";
import {
  CAPTCHA_FAILED_COMPLETE_MSG,
  HOMEPAGE_CAPTCHA_SUCCESS_EVENT,
  clearErrorAfterCaptchaSuccess,
  ensureHomepageCaptchaSuccess,
  isCaptchaVerificationError,
  requestHomepageCaptchaRelock,
} from "./HomepageCaptchaGate";
import { TrainSampleDataModal } from "./TrainSampleDataModal";

function BuyerFieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="field-error buy-model-contact-error">{message}</p>;
}

type RecommendedTarget = {
  column?: string;
  name?: string;
  label?: string;
  score?: number;
  reason?: string;
  top_predictor?: string;
  max_predictor_correlation?: number;
};

type AnalyzeResult = {
  session_id: string;
  total_rows: number;
  total_columns: number;
  recommended_targets?: RecommendedTarget[];
  correlation_pair?: {
    feature_a: string;
    feature_b: string;
    correlation: number;
    abs_correlation?: number;
  };
  correlation_highlights?: { feature_a: string; feature_b: string; correlation: number }[];
  preview?: { columns: string[]; data: unknown[][] };
  data_prep?: {
    row_count?: number;
    column_count?: number;
    warnings?: string[];
    missing_rates?: Record<string, number>;
  };
  task_hints?: { by_column?: Record<string, string> };
  engine?: string;
};

type HumanSummary = {
  tone?: "good" | "warn" | "bad" | string;
  headline?: string;
  body?: string;
  usable?: boolean;
};

type DriftReport = {
  drift_score?: number;
  alert?: boolean;
  likely_type?: string | null;
  guidance?: string;
  fired?: string[];
  monitors?: {
    psi?: { max_psi?: number; alert?: boolean };
    ks_predictions?: { p_value?: number; alert?: boolean };
    null_rate?: { max_delta?: number; alert?: boolean };
  };
};

type ClassificationMetrics = {
  accuracy?: number;
  precision?: number;
  recall?: number;
  f1?: number;
  positive_label?: number | string;
  definitions?: Record<string, string>;
};

type TrainResults = {
  accuracy?: number;
  mae?: number;
  rmse?: number;
  confidence?: number;
  production_ready?: boolean;
  target_feature?: string;
  task_type?: string;
  training_method?: string;
  model_selector?: string;
  feature_importance?: { feature: string; importance: number }[];
  sample_predictions?: { prediction: number; actual: number }[];
  data_quality_warnings?: string[];
  data_prep?: { warnings?: string[] };
  classification_metrics?: ClassificationMetrics | null;
  regression_metrics?: { r2?: number; mae?: number; rmse?: number; note?: string } | null;
  drift?: DriftReport | null;
  drift_url?: string;
  predictor_features?: string[];
  feature_correlations?: { feature: string; correlation?: number; abs_correlation?: number }[];
  model_path?: string;
  predict_url?: string;
  schema_url?: string;
  human_summary?: HumanSummary;
};

type TrainResult = {
  session_id: string;
  production_ready?: boolean;
  results?: TrainResults;
  message?: string;
  engine?: string;
};

type PredictResult = {
  prediction?: number;
  target_feature?: string;
  confidence?: number;
  interpretation?: string;
  message?: string;
};

type DocCategory = "leases" | "resumes";
type AgentWorkflow = "leases" | "resumes" | "job_search";

const JOB_SOURCE_LABELS: Record<string, string> = {
  remoteok: "Remote OK",
  craigslist: "Craigslist",
  simplyhired: "SimplyHired",
  wellfound: "Wellfound",
  geebo: "Geebo",
  google: "Google Jobs",
};

type DocSessionFile = {
  index?: number;
  filename?: string;
  stored_as?: string;
  bytes?: number;
  char_count?: number;
  pdf_url?: string | null;
};

type DocSession = {
  session_id: string;
  filename?: string;
  filenames?: string[];
  file_count?: number;
  files?: DocSessionFile[];
  chunk_count?: number;
  preview?: string;
  message?: string;
  doc_category?: DocCategory | string;
  resume_job_category?: string | null;
  category_warning?: string | null;
  char_count?: number;
  pdf_url?: string | null;
  lease_id?: string | null;
  lease_fields_extractor?: string | null;
};

type LeaseTableColumn = { key: string; label: string };

type LeaseResultsTable = {
  columns?: LeaseTableColumn[];
  rows?: Record<string, string | number | null | undefined>[];
  row_count?: number;
};

type LeaseToolResult = {
  status?: string;
  action?: string;
  method?: string;
  message?: string;
  formatted?: string;
  city?: string;
  pdf_url?: string;
  pdf_download?: string;
  hit_pdf_url?: string | null;
  corpus_pdf_url?: string | null;
  pdf_filename?: string;
  accuracy?: number;
  matches?: number;
  total_fields?: number;
  match_count?: number;
  query?: string;
  file_count?: number;
  delinquent_count?: number;
  total_leases?: number;
  total_balance_owed?: number;
  table?: LeaseResultsTable | null;
  fields_list?: Record<string, unknown>[];
  llm_evaluation?: {
    status?: string;
    mode?: string;
    model?: string;
    narrative?: string;
    portfolio_summary?: string;
    risk_themes?: string[];
    message?: string;
    reason?: string;
  };
};

const LEASE_PROBLEM_UNIT_SIZE =
  "What unit sizes should a self-storage facility offer, and how should it price them?";
const LEASE_PROBLEM_DELINQUENCY =
  "How much revenue is owed, and which accounts need attention?";

/** Public storage footprint range: 5x5 (25) … 10x30 (300). */
const LEASE_SQFT_PLOT_MIN = 25;
const LEASE_SQFT_PLOT_MAX = 300;
const LEASE_PRICE_PLOT_MIN = 0;
const LEASE_PRICE_PLOT_MAX = 300;
/** Common retail unit sizes for axis ticks. */
const LEASE_SQFT_TICKS = [25, 50, 75, 100, 150, 200, 250, 300];
const LEASE_PRICE_TICKS = [0, 50, 100, 150, 200, 250, 300];
/** Standard unit size → retail dimensions label. */
const LEASE_STORAGE_SIZE_LABEL: Record<number, string> = {
  25: "5×5",
  50: "5×10",
  75: "5×15",
  100: "10×10",
  150: "10×15",
  200: "10×20",
  250: "10×25",
  300: "10×30",
};

function leaseNearestStandardSqft(sqft: number): number {
  return LEASE_SQFT_TICKS.reduce((best, s) =>
    Math.abs(s - sqft) < Math.abs(best - sqft) ? s : best,
  );
}

function leaseStorageLabel(sqft: number): string {
  const std = leaseNearestStandardSqft(sqft);
  const dims = LEASE_STORAGE_SIZE_LABEL[std] || `${std}`;
  return `${dims} · ${std} sq ft`;
}

/** Human headline from structured inventory fields (never “plateau ~…”). */
function leaseOptimalSizeHeadline(inv: {
  summary?: string;
  optimal_unit_sqft?: number | null;
  price_per_sqft?: number | null;
  recommended_total_units?: number | null;
  demand_metrics?: { population_density_per_sq_mi?: number | null } | null;
} | null | undefined): string | null {
  if (!inv) return null;
  const bits: string[] = [];
  const size = inv.optimal_unit_sqft;
  const pps = inv.price_per_sqft;
  if (size != null && Number.isFinite(size) && pps != null && Number.isFinite(pps)) {
    bits.push(
      `Optimal calculated size ~${Math.round(Number(size))} sq ft @ $${Number(pps).toFixed(2)}/sq ft`,
    );
  }
  const units = inv.recommended_total_units;
  if (units != null && Number.isFinite(units)) {
    bits.push(`~${Math.round(Number(units))} units recommended`);
  }
  const dens = inv.demand_metrics?.population_density_per_sq_mi;
  if (dens != null && Number.isFinite(dens)) {
    bits.push(
      `density ${Number(dens).toLocaleString(undefined, { maximumFractionDigits: 0 })}/sq mi`,
    );
  }
  if (bits.length) return bits.join("; ");
  const raw = (inv.summary || "").trim();
  if (!raw) return null;
  return raw.replace(/\bplateau\b/gi, "Optimal calculated size");
}

/** Group stepped price curve into flat plateaus (constant $/mo across a sq ft band). */
function leaseDetectPricePlateaus(
  points: { sqft: number; prediction: number }[],
): { startSqft: number; endSqft: number; price: number }[] {
  const sorted = [...points]
    .filter((p) => Number.isFinite(p.sqft) && Number.isFinite(p.prediction))
    .sort((a, b) => a.sqft - b.sqft);
  if (!sorted.length) return [];
  const eps = 0.75; // $ tolerance for “same price” step
  const out: { startSqft: number; endSqft: number; price: number }[] = [];
  let i = 0;
  while (i < sorted.length) {
    const price = sorted[i].prediction;
    let j = i;
    while (
      j + 1 < sorted.length &&
      Math.abs(sorted[j + 1].prediction - price) <= eps
    ) {
      j += 1;
    }
    const startSqft = sorted[i].sqft;
    const endSqft = sorted[j].sqft;
    // Keep plateaus that cover a real band (or at least one step jump).
    if (endSqft - startSqft >= 8 || j > i) {
      out.push({ startSqft, endSqft, price });
    }
    i = j + 1;
  }
  return out;
}

function LeaseSqftPricePlot({
  points,
  flatAt,
}: {
  points: { sqft: number; prediction: number }[];
  flatAt?: number | null;
}) {
  if (!points.length) return null;
  const w = 720;
  const h = 280;
  const pad = { t: 36, r: 20, b: 44, l: 52 };
  const xMin = LEASE_SQFT_PLOT_MIN;
  const xMax = LEASE_SQFT_PLOT_MAX;
  const yMin = LEASE_PRICE_PLOT_MIN;
  const yMax = LEASE_PRICE_PLOT_MAX;
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  const sx = (x: number) =>
    pad.l + ((x - xMin) / xSpan) * (w - pad.l - pad.r);
  const sy = (y: number) =>
    pad.t + (1 - (y - yMin) / ySpan) * (h - pad.t - pad.b);
  const clipped = points.filter(
    (p) =>
      Number.isFinite(p.sqft) &&
      Number.isFinite(p.prediction) &&
      p.sqft >= xMin &&
      p.sqft <= xMax,
  );
  const poly = clipped
    .map((p) => {
      const y = Math.min(yMax, Math.max(yMin, p.prediction));
      return `${sx(p.sqft).toFixed(1)},${sy(y).toFixed(1)}`;
    })
    .join(" ");
  const plateaus = leaseDetectPricePlateaus(clipped);
  const flatInRange =
    flatAt != null && Number.isFinite(flatAt) && flatAt >= xMin && flatAt <= xMax;
  const flatX = flatInRange ? sx(flatAt!) : null;
  return (
    <div className="lease-sqft-plot-wrap">
      <svg
        className="lease-sqft-plot"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label="Predicted rent versus public storage unit size: 25 to 300 sq ft"
      >
        {LEASE_SQFT_TICKS.map((t) => (
          <line
            key={`vx-${t}`}
            x1={sx(t)}
            y1={pad.t}
            x2={sx(t)}
            y2={h - pad.b}
            stroke="rgba(255,255,255,0.08)"
          />
        ))}
        {LEASE_PRICE_TICKS.map((t) => (
          <line
            key={`hy-${t}`}
            x1={pad.l}
            y1={sy(t)}
            x2={w - pad.r}
            y2={sy(t)}
            stroke="rgba(255,255,255,0.08)"
          />
        ))}
        <line
          x1={pad.l}
          y1={h - pad.b}
          x2={w - pad.r}
          y2={h - pad.b}
          stroke="rgba(255,255,255,0.35)"
        />
        <line
          x1={pad.l}
          y1={pad.t}
          x2={pad.l}
          y2={h - pad.b}
          stroke="rgba(255,255,255,0.35)"
        />
        {poly ? (
          <polyline fill="none" stroke="#5eead4" strokeWidth="2" points={poly} />
        ) : null}
        {clipped.map((p) => {
          const y = Math.min(yMax, Math.max(yMin, p.prediction));
          const isFlat =
            flatAt != null && Math.round(p.sqft) === Math.round(flatAt);
          return (
            <circle
              key={`pt-${p.sqft}`}
              cx={sx(p.sqft)}
              cy={sy(y)}
              r={isFlat ? 3.5 : 2}
              fill={isFlat ? "#fbbf24" : "#5eead4"}
              stroke={isFlat ? "#fbbf24" : "rgba(8,20,28,0.85)"}
              strokeWidth={1}
            >
              <title>{`${leaseStorageLabel(p.sqft)} → $${p.prediction.toFixed(2)}`}</title>
            </circle>
          );
        })}
        {plateaus.map((pl, idx) => {
          const midSqft = (pl.startSqft + pl.endSqft) / 2;
          const labelSqft = leaseNearestStandardSqft(pl.startSqft);
          const dims = LEASE_STORAGE_SIZE_LABEL[labelSqft] || `${labelSqft}`;
          const y = Math.min(yMax, Math.max(yMin, pl.price));
          const cx = sx(midSqft);
          const cy = sy(y);
          // Alternate label above/below the step so crowded plateaus stay readable.
          const above = idx % 2 === 0;
          const ty = above
            ? Math.max(pad.t + 10, cy - 14)
            : Math.min(h - pad.b - 6, cy + 16);
          return (
            <g key={`pl-${pl.startSqft}-${pl.endSqft}`}>
              <title>{`${dims} (${labelSqft} sq ft) ≈ $${pl.price.toFixed(0)}/mo`}</title>
              <text
                x={cx}
                y={ty}
                fill="rgba(255,255,255,0.88)"
                fontSize="10"
                textAnchor="middle"
              >
                {dims} · {labelSqft} sq ft
              </text>
            </g>
          );
        })}
        {flatX != null ? (
          <>
            <line
              x1={flatX}
              y1={pad.t}
              x2={flatX}
              y2={h - pad.b}
              stroke="#fbbf24"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
            <text
              x={Math.min(flatX + 6, w - pad.r - 110)}
              y={pad.t + 12}
              fill="#fbbf24"
              fontSize="11"
            >
              optimal {leaseStorageLabel(flatAt!)}
            </text>
          </>
        ) : null}
        {LEASE_SQFT_TICKS.map((t) => (
          <text
            key={`x-${t}`}
            x={sx(t)}
            y={h - 14}
            fill="rgba(255,255,255,0.55)"
            fontSize="10"
            textAnchor="middle"
          >
            {t}
          </text>
        ))}
        {LEASE_PRICE_TICKS.map((t) => (
          <text
            key={`y-${t}`}
            x={pad.l - 6}
            y={sy(t) + 3}
            fill="rgba(255,255,255,0.55)"
            fontSize="10"
            textAnchor="end"
          >
            ${t}
          </text>
        ))}
        <text
          x={(pad.l + w - pad.r) / 2}
          y={h - 2}
          fill="rgba(255,255,255,0.4)"
          fontSize="10"
          textAnchor="middle"
        >
          sq ft (public storage 25–300: 5×5 … 10×30)
        </text>
      </svg>
    </div>
  );
}

function leaseSortComparable(raw: unknown): { n: number | null; s: string } {
  if (raw === null || raw === undefined || raw === "") {
    return { n: null, s: "" };
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { n: raw, s: String(raw) };
  }
  const s = String(raw).trim();
  if (!s || s === "—") {
    return { n: null, s: "" };
  }
  const moneyish = s.replace(/[$,\s]/g, "");
  if (/^-?\d+(\.\d+)?$/.test(moneyish) && (/[$£€,]/.test(s) || /^-?\d+(\.\d+)?$/.test(s))) {
    const n = Number(moneyish);
    if (!Number.isNaN(n)) return { n, s: s.toLowerCase() };
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return { n: t, s: s.toLowerCase() };
  }
  if (/[A-Za-z]/.test(s) && /\d{4}/.test(s)) {
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return { n: t, s: s.toLowerCase() };
  }
  return { n: null, s: s.toLowerCase() };
}

function compareLeaseRows(
  a: Record<string, string | number | null | undefined>,
  b: Record<string, string | number | null | undefined>,
  key: string,
  dir: "asc" | "desc",
): number {
  const av = leaseSortComparable(a[key]);
  const bv = leaseSortComparable(b[key]);
  let cmp = 0;
  if (av.s === "" && bv.s !== "") cmp = 1;
  else if (bv.s === "" && av.s !== "") cmp = -1;
  else if (av.n != null && bv.n != null) cmp = av.n - bv.n;
  else if (av.n != null) cmp = -1;
  else if (bv.n != null) cmp = 1;
  else cmp = av.s.localeCompare(bv.s, undefined, { numeric: true, sensitivity: "base" });
  return dir === "asc" ? cmp : -cmp;
}

function rowMatchesLeaseFilter(
  row: Record<string, string | number | null | undefined>,
  columns: LeaseTableColumn[],
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  for (const col of columns) {
    if (col.key === "pdf_url") continue;
    const raw = row[col.key];
    if (raw === null || raw === undefined || raw === "") continue;
    if (String(raw).toLowerCase().includes(q)) return true;
  }
  return false;
}

function LeaseToolResultsTable({
  table,
  city,
  action,
  delinquentCount,
  totalBalanceOwed,
  totalLeases,
}: {
  table: LeaseResultsTable;
  city?: string;
  action?: string;
  delinquentCount?: number;
  totalBalanceOwed?: number;
  totalLeases?: number;
}) {
  const columns = table.columns || [];
  const rows = table.rows || [];
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filterQ, setFilterQ] = useState("");

  useEffect(() => {
    setSortKey(null);
    setSortDir("asc");
    setFilterQ("");
  }, [table]);

  if (!columns.length) return null;

  const filtered = filterQ.trim()
    ? rows.filter((row) => rowMatchesLeaseFilter(row, columns, filterQ))
    : rows;
  const sorted =
    sortKey != null
      ? [...filtered].sort((a, b) => compareLeaseRows(a, b, sortKey, sortDir))
      : filtered;

  function onHeaderClick(key: string) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  const cityName = (city || "").trim();
  const total = rows.length;
  const shown = sorted.length;
  const isDelinquency = action === "delinquency";
  const showToolbar = Boolean(cityName) || action === "list" || isDelinquency;

  let countLabel = "";
  if (isDelinquency) {
    const overdue =
      typeof delinquentCount === "number" ? delinquentCount : total;
    countLabel = filterQ.trim()
      ? `${shown} of ${overdue} overdue`
      : `${overdue} overdue`;
    if (cityName) countLabel += ` · ${cityName}`;
    if (typeof totalBalanceOwed === "number" && !filterQ.trim()) {
      countLabel += ` · $${totalBalanceOwed.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} owed`;
    }
  } else {
    countLabel = filterQ.trim()
      ? `${shown} of ${total} lease${total === 1 ? "" : "s"}`
      : `${total} lease${total === 1 ? "" : "s"}`;
    if (cityName) countLabel += ` · ${cityName}`;
    else if (typeof totalLeases === "number" && totalLeases !== total) {
      countLabel += ` of ${totalLeases}`;
    }
  }

  return (
    <div className="lease-results-block">
      {showToolbar ? (
        <div className="lease-results-toolbar">
          <h3 className="lease-results-count">{countLabel}</h3>
          <div className="lease-results-filter">
            <label className="visually-hidden" htmlFor="lease-table-filter">
              Filter leases
            </label>
            <input
              id="lease-table-filter"
              type="search"
              value={filterQ}
              onChange={(ev) => setFilterQ(ev.target.value)}
              placeholder="Filter leases…"
              autoComplete="off"
            />
            {filterQ ? (
              <button
                type="button"
                className="lease-results-filter-clear"
                onClick={() => setFilterQ("")}
                title="Clear filter — show all records"
                aria-label="Clear filter"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {total === 0 ? (
        <p className="buy-server-product-note">No leases to display.</p>
      ) : shown === 0 ? (
        <p className="buy-server-product-note">
          No leases match “{filterQ.trim()}”.{" "}
          <button
            type="button"
            className="linkish"
            onClick={() => setFilterQ("")}
          >
            Clear filter
          </button>
        </p>
      ) : (
        <div className="lease-results-table-wrap">
          <table className="lease-results-table">
            <thead>
              <tr>
                {columns.map((col) => {
                  const active = sortKey === col.key;
                  const ariaSort = active
                    ? sortDir === "asc"
                      ? "ascending"
                      : "descending"
                    : "none";
                  return (
                    <th key={col.key} scope="col" aria-sort={ariaSort}>
                      <button
                        type="button"
                        className={`lease-th-sort${
                          active ? " lease-th-sort--active" : ""
                        }`}
                        onClick={() => onHeaderClick(col.key)}
                        title={
                          active
                            ? `Sorted ${
                                sortDir === "asc" ? "ascending" : "descending"
                              }; click to reverse`
                            : `Sort by ${col.label}`
                        }
                      >
                        <span>{col.label}</span>
                        <span className="lease-sort-arrow" aria-hidden="true">
                          {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={`${row.source_file || row.stored_as || i}-${i}`}>
                  {columns.map((col) => {
                    const raw = row[col.key];
                    const text =
                      raw === null || raw === undefined || raw === ""
                        ? "—"
                        : String(raw);
                    if (col.key === "pdf_url" && typeof raw === "string" && raw) {
                      return (
                        <td key={col.key}>
                          <a
                            href={raw}
                            download={String(row.source_file || "lease.pdf")}
                          >
                            PDF
                          </a>
                        </td>
                      );
                    }
                    return <td key={col.key}>{text}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function targetLabel(t: RecommendedTarget): string {
  return t.name || t.column || "";
}

function targetOptionText(t: RecommendedTarget): string {
  return t.label || t.name || t.column || "";
}

const BUY_MODEL_PACKAGES = [
  {
    product: "Free",
    title: "Free 15 day trial — $15 refundable deposit if canceled before trial ends",
    price: "$15 refundable deposit · canceled before trial ends",
    paypalAmount: "15.00",
    details: [
      { label: "Models", value: "1" },
      { label: "Predictions", value: "5/mo" },
      { label: "Training data", value: "10 MB/model" },
      { label: "Retraining jobs", value: "1/mo/model" },
      { label: "Compute", value: "0.5 CPU-hours/job" },
      { label: "Hosting", value: "Shared" },
      { label: "Monitoring", value: "Basic" },
      { label: "SLA", value: "—" },
    ],
  },
  {
    product: "Professional",
    title: "Professional — $499/mo",
    price: "$499/month",
    paypalAmount: "499.00",
    details: [
      { label: "Models", value: "2" },
      { label: "Predictions", value: "30/mo" },
      { label: "Training data", value: "50 MB/model" },
      { label: "Retraining jobs", value: "5/mo/model" },
      { label: "Compute", value: "2 CPU-hours/job" },
      { label: "Hosting", value: "Private" },
      { label: "Monitoring", value: "Drift + monitoring" },
      { label: "SLA", value: "Standard" },
    ],
  },
  {
    product: "Business",
    title: "Business — $1,500/mo",
    price: "$1,500/month",
    paypalAmount: "1500.00",
    details: [
      { label: "Models", value: "5" },
      { label: "Predictions", value: "100/mo" },
      { label: "Training data", value: "150 MB/model" },
      { label: "Retraining jobs", value: "30/mo/model" },
      { label: "Compute", value: "10 CPU-hours/job" },
      { label: "Hosting", value: "Private" },
      { label: "Monitoring", value: "Advanced" },
      { label: "SLA", value: "Premium" },
    ],
  },
  {
    product: "Enterprise",
    title: "Enterprise — Custom",
    price: "Custom Proposal",
    paypalAmount: null as string | null,
    details: [
      { label: "Models", value: "Custom" },
      { label: "Predictions", value: "Custom" },
      { label: "Training data", value: "Custom" },
      { label: "Retraining jobs", value: "Custom" },
      { label: "Compute", value: "Custom" },
      { label: "Hosting", value: "Custom" },
      { label: "Monitoring", value: "Custom" },
      { label: "SLA", value: "Custom" },
    ],
  },
] as const;

type Props = {
  /** Hide the long engine blurb when the parent card already has a short lead. */
  compact?: boolean;
  /** Lock to CSV train or document agent (hides Make AI / Ask AI path chooser). */
  lockedPath?: UploadPath;
};

export function TrainModelForm({ compact, lockedPath }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState<File[]>([]);
  const file = files[0] ?? null;
  const [uploadIntent, setUploadIntent] = useState<UploadPath>(lockedPath ?? "train");
  const pathLocked = Boolean(lockedPath);
  const [docCategory, setDocCategory] = useState<DocCategory>("leases");
  const [agentWorkflow, setAgentWorkflow] = useState<AgentWorkflow>("leases");
  const [analyze, setAnalyze] = useState<AnalyzeResult | null>(null);
  const [docSession, setDocSession] = useState<DocSession | null>(null);
  const [leaseToolResult, setLeaseToolResult] = useState<LeaseToolResult | null>(null);
  /** Lease-agent train result (stays in Ask AI lease workflow — not Make AI steps). */
  const [leaseTrained, setLeaseTrained] = useState<TrainResult | null>(null);
  const [leasePredictInputs, setLeasePredictInputs] = useState<Record<string, string>>({});
  const [leasePrediction, setLeasePrediction] = useState<PredictResult | null>(null);
  const [leasePredictError, setLeasePredictError] = useState<string | null>(null);
  const [leaseTrainMeta, setLeaseTrainMeta] = useState<{
    target_feature?: string;
    correlation_pair?: { feature_a?: string; feature_b?: string; correlation?: number; abs_correlation?: number };
    message?: string;
  } | null>(null);
  const [leaseSqftAnalysis, setLeaseSqftAnalysis] = useState<{
    message?: string;
    flat_at_sqft?: number | null;
    points?: { sqft: number; prediction: number }[];
    sweep_start?: number;
    sweep_end?: number;
    target_feature?: string;
    predictor?: string;
    city?: string | null;
    curve_note?: string;
    inventory_recommendation?: {
      summary?: string;
      summary_sections?: { title?: string; items?: string[] }[];
      optimal_unit_sqft?: number | null;
      plateau_sqft?: number | null;
      plateau_monthly_rent?: number | null;
      price_per_sqft?: number | null;
      historic_lease_count?: number;
      leases_per_1000_residents?: number | null;
      projected_lease_demand?: number | null;
      recommended_total_units?: number | null;
      recommended_mix?: {
        band?: string;
        label?: string;
        recommended_units?: number;
        historic_share?: number;
      }[];
      population?: {
        current?: number | null;
        projected?: number | null;
        horizon_years?: number | null;
        note?: string | null;
        source?: string | null;
        label?: string | null;
      };
      demand_metrics?: {
        population_density_per_sq_mi?: number | null;
        churn_rate_pct?: number | null;
        renter_ratio_pct?: number | null;
        land_area_sq_mi?: number | null;
        demand_multiplier?: number | null;
        storage_score?: number | null;
        storage_tier?: string | null;
        takeaways?: string[];
        data_sources_hint?: string;
        profile_url?: string | null;
      };
    } | null;
  } | null>(null);
  const [leaseCities, setLeaseCities] = useState<
    { city: string; lease_count: number }[]
  >([]);
  const [leaseCity, setLeaseCity] = useState<string>("");
  const [leaseZip, setLeaseZip] = useState("");
  const [leasePopCurrent, setLeasePopCurrent] = useState("");
  const [leasePopProjected, setLeasePopProjected] = useState("");
  const [target, setTarget] = useState("");
  const [resumeJdText, setResumeJdText] = useState("");
  const [jobSearchNote, setJobSearchNote] = useState("");
  const [jobSearchRows, setJobSearchRows] = useState<
    Array<{
      title?: string;
      source?: string;
      company?: string;
      location?: string;
      url?: string;
      match_pct?: number;
      recommendation?: string;
    }>
  | null>(null);
  const [resumeJdEval, setResumeJdEval] = useState<{
    overall_match_pct?: number;
    mandatory_met?: boolean;
    evidence_coverage_pct?: number;
    recommendation?: string;
    match_label?: string;
    candidate?: string;
    role?: string;
    strengths?: string[];
    achievements?: string[];
    categories?: Array<{
      id?: string;
      name?: string;
      weight_pct?: number;
      score_pct?: number;
      weighted_points?: number;
      summary?: string;
    }>;
    mandatory_status?: Array<{
      req_id?: string;
      text?: string;
      status?: string;
      note?: string;
    }>;
    advantages?: string[];
    gaps?: string[];
    limitations?: string[];
    career_track?: {
      resume_title?: string;
      resume_level?: string;
      resume_track?: string;
      resume_label?: string;
      jd_title?: string;
      jd_level?: string;
      jd_track?: string;
      jd_label?: string;
      relation?: string;
      note?: string;
    };
    screening_category?: string;
    screening_confidence?: number;
    screening_source?: string;
    jd_role?: string;
    jd_role_confidence?: number;
    jd_role_reliable?: boolean;
    jd_role_note?: string;
    evaluation_quality_pct?: number;
    evaluation_quality_label?: string;
    evaluation_quality_reasons?: string[];
    harness_intervene?: boolean;
    harness_intervention?: { skill?: string; actions?: string[] };
    llm_evaluation?: {
      status?: string;
      mode?: string;
      model?: string;
      narrative?: string;
      skill_fit_pct?: number;
      team_fit_pct?: number;
      skill_fit_note?: string;
      team_fit_note?: string;
      message?: string;
      reason?: string;
    };
    requirements?: Array<{
      req_id?: string;
      category?: string;
      text?: string;
      score?: number;
      evidence_resume?: string;
      notes?: string;
    }>;
  } | null>(null);
  const [busy, setBusy] = useState<
    | "analyze"
    | "ingest"
    | "train"
    | "sample"
    | "predict"
    | "lease"
    | "resume"
    | "jobs"
    | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [trained, setTrained] = useState<TrainResult | null>(null);
  const [predictInputs, setPredictInputs] = useState<Record<string, string>>({});
  const [prediction, setPrediction] = useState<PredictResult | null>(null);
  const [predictError, setPredictError] = useState<string | null>(null);
  const [calendlyError, setCalendlyError] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<1 | 2 | 3 | 4>(1);
  const [buyPackage, setBuyPackage] = useState<string>(BUY_MODEL_PACKAGES[0].product);
  const [buyNote, setBuyNote] = useState<string | null>(null);
  const [buyPackageExpanded, setBuyPackageExpanded] = useState<Record<string, boolean>>({});
  const [buyPaypalLoading, setBuyPaypalLoading] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerFieldErrors, setBuyerFieldErrors] = useState<Record<string, string>>({});
  const [sampleDataModalOpen, setSampleDataModalOpen] = useState(false);

  const selectedBuyPkg = BUY_MODEL_PACKAGES.find((p) => p.product === buyPackage);
  const buyPaypalAmount = selectedBuyPkg?.paypalAmount ?? null;

  const results = trained?.results;
  const ready = Boolean(trained?.production_ready || results?.production_ready);
  const features = results?.predictor_features || [];
  const summary = results?.human_summary;
  const predictUrl =
    results?.predict_url ||
    (trained?.session_id ? `/api/train/models/${trained.session_id}/predict` : "");
  const leaseResults = leaseTrained?.results;
  const leaseReady = Boolean(
    leaseTrained?.production_ready || leaseResults?.production_ready,
  );
  const leaseFeatures = leaseResults?.predictor_features || [];
  const leaseSummary = leaseResults?.human_summary;

  // Keep the open accordion step below the sticky header (load / step changes).
  useEffect(() => {
    const reveal = () => {
      const el = document.getElementById(`train-guide-tab-${openStep}`);
      if (!el) return;
      const headerH = document.querySelector(".header")?.getBoundingClientRect().height ?? 0;
      const top = el.getBoundingClientRect().top;
      // Obscured under nav or scrolled above the viewport.
      if (top < headerH + 8) {
        window.scrollBy({ top: top - headerH - 12, left: 0, behavior: "auto" });
      }
    };
    reveal();
    // Beat browser scroll restoration that can run after the first paint.
    const t = window.setTimeout(reveal, 0);
    return () => window.clearTimeout(t);
  }, [openStep]);

  useEffect(() => {
    if (!features.length) {
      setPredictInputs({});
      return;
    }
    setPredictInputs((prev) => {
      const next: Record<string, string> = {};
      for (const f of features) {
        next[f] = prev[f] ?? "1";
      }
      return next;
    });
    setPrediction(null);
    setPredictError(null);
  }, [trained?.session_id, features.join("|")]);

  useEffect(() => {
    const onCaptchaSuccess = () => {
      setError((current) => clearErrorAfterCaptchaSuccess(current));
    };
    window.addEventListener(HOMEPAGE_CAPTCHA_SUCCESS_EVENT, onCaptchaSuccess);
    return () =>
      window.removeEventListener(HOMEPAGE_CAPTCHA_SUCCESS_EVENT, onCaptchaSuccess);
  }, []);

  // City dropdown: folder counts from BE (independent of upload session).
  useEffect(() => {
    if (docCategory === "leases") {
      void loadLeaseCities();
    } else {
      setLeaseCities([]);
      setLeaseCity("");
    }
  }, [docCategory]);

  useEffect(() => {
    const ctx = searchParams.get("context");
    if (ctx !== "model_access") return;

    const payment = searchParams.get("payment");
    const orderId = searchParams.get("order");
    const reason = searchParams.get("reason");
    if (!payment) return;

    setOpenStep(4);

    if (payment === "cancelled") {
      setError("PayPal checkout was cancelled. You can try again when ready.");
      setSearchParams({}, { replace: true });
      return;
    }

    if (payment === "error") {
      if (orderId) {
        setBuyPaypalLoading(true);
        apiFetch(`/api/train/model-access/orders/${encodeURIComponent(orderId)}`)
          .then((r) => r.json())
          .then((data) => {
            const payErr =
              (data?.payment && typeof data.payment.error === "string" && data.payment.error) ||
              null;
            setError(
              payErr ||
                (reason === "missing_paypal_id"
                  ? "PayPal did not return a payment id. Please try checkout again."
                  : "PayPal payment did not complete. Please try again or contact us."),
            );
          })
          .catch(() =>
            setError("PayPal payment did not complete. Please try again or contact us."),
          )
          .finally(() => {
            setBuyPaypalLoading(false);
            setSearchParams({}, { replace: true });
          });
        return;
      }
      setError("PayPal payment did not complete. Please try again or contact us.");
      setSearchParams({}, { replace: true });
      return;
    }

    if (payment === "success" && orderId) {
      setBuyPaypalLoading(true);
      apiFetch(`/api/train/model-access/orders/${encodeURIComponent(orderId)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data?.id) {
            setError("Payment succeeded but we could not load your order.");
            return;
          }
          if (data.status === "payment_failed" || data.payment?.error) {
            setError(
              data.payment?.error ||
                "PayPal capture failed after approval. Contact us with your order id.",
            );
            return;
          }
          setBuyNote(
            `Paid — ${data.package_label || "Model access"} · ${data.price_label || ""}`.trim(),
          );
          setError(null);
        })
        .catch(() =>
          setError("Payment may have succeeded — refresh or contact us with your order id."),
        )
        .finally(() => {
          setBuyPaypalLoading(false);
          setSearchParams({}, { replace: true });
        });
    }
  }, [searchParams, setSearchParams]);

  function clearBuyerFieldError(key: string) {
    setBuyerFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateBuyerContact(): Record<string, string> {
    const formErrors = validateCustomerForm({
      name: buyerName,
      email: buyerEmail,
      phone: buyerPhone,
    });
    const errs: Record<string, string> = {};
    if (formErrors.name) errs.buyer_name = formErrors.name;
    if (formErrors.email) errs.buyer_email = formErrors.email;
    if (formErrors.phone) errs.buyer_phone = formErrors.phone;
    return errs;
  }

  async function startModelAccessPayPal() {
    if (!trained?.session_id) {
      setError("Train a model in step 3 before checkout.");
      return;
    }
    const isEnterprise = buyPackage === "Enterprise";
    if (!buyPaypalAmount && !isEnterprise) {
      setError("Choose a package with PayPal checkout (Free trial deposit, Professional, or Business).");
      return;
    }
    const contactErrors = validateBuyerContact();
    setBuyerFieldErrors(contactErrors);
    if (Object.keys(contactErrors).length > 0) {
      setError("Please correct the highlighted contact fields.");
      return;
    }
    setError(null);
    setBuyNote(null);
    setBuyPaypalLoading(true);
    let redirecting = false;
    try {
      const res = await apiFetch("/api/train/model-access/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          package: buyPackage,
          session_id: trained.session_id,
          buyer_name: buyerName.trim(),
          buyer_email: buyerEmail.trim(),
          buyer_phone: buyerPhone.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        if (data.field_errors && typeof data.field_errors === "object") {
          setBuyerFieldErrors(data.field_errors as Record<string, string>);
        }
        throw new Error(
          (typeof data.detail === "string" && data.detail) ||
            (typeof data.error === "string" && data.error) ||
            "We could not start checkout right now.",
        );
      }
      // Enterprise: email-only inquiry (no PayPal).
      if (data.enterprise_inquiry) {
        const pageUrl =
          typeof data.api_links?.predict_page_url === "string"
            ? data.api_links.predict_page_url
            : "";
        setBuyNote(
          pageUrl
            ? `Enterprise inquiry sent. Try predictions: ${pageUrl}`
            : "Enterprise inquiry sent with your contact details. We will follow up shortly.",
        );
        return;
      }
      // Paid packages: email already sent server-side; then redirect to PayPal.
      redirectToPayPalApprove(data.paypal?.approve_url);
      redirecting = true;
    } catch (err) {
      setError(userFacingFetchError(err, "Could not start checkout."));
    } finally {
      if (!redirecting) setBuyPaypalLoading(false);
    }
  }

  async function runAnalyze(csvFile: File) {
    setError(null);
    setTrained(null);
    setPrediction(null);
    setDocSession(null);
    setBusy("analyze");
    try {
      assertUploadForPath(csvFile, "train");
      setUploadIntent("train");
      const gateCheck = await ensureHomepageCaptchaSuccess();
      if (!gateCheck.ok) {
        setError(gateCheck.message || CAPTCHA_FAILED_COMPLETE_MSG);
        return;
      }
      const body = new FormData();
      body.append("file", csvFile);
      if (gateCheck.gate) {
        body.append("homepage_gate_token", gateCheck.gate);
      }
      const res = await apiFetch("/api/train/analyze", { method: "POST", body });
      const raw = await res.text();
      let data: {
        status?: string;
        message?: string;
        captcha_ok?: boolean;
        captcha_required?: boolean;
        recommended_targets?: RecommendedTarget[];
      } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok || data.status === "error") {
        const msg = analyzeErrorMessage(res, raw, data);
        if (isCaptchaVerificationError(msg, data)) {
          requestHomepageCaptchaRelock();
          throw new Error(data.message || CAPTCHA_FAILED_COMPLETE_MSG);
        }
        throw new Error(msg);
      }
      setAnalyze(data as AnalyzeResult);
      const first = (data.recommended_targets || [])[0];
      setTarget(first ? targetLabel(first) : "");
      setOpenStep(2);
    } catch (err) {
      const msg = userFacingFetchError(err, "Analyze failed.");
      setError(msg);
      setAnalyze(null);
      if (isCaptchaVerificationError(msg)) requestHomepageCaptchaRelock();
    } finally {
      setBusy(null);
    }
  }

  async function runDocIngest(docFiles: File[]) {
    let startedJobSearch = "";
    setError(null);
    setAnalyze(null);
    setTrained(null);
    setPrediction(null);
    setLeaseToolResult(null);
    setLeaseTrained(null);
    setLeaseTrainMeta(null);
    setLeaseSqftAnalysis(null);
    setLeasePrediction(null);
    setLeasePredictError(null);
    setLeasePredictInputs({});
    setResumeJdEval(null);
    setJobSearchRows(null);
    setJobSearchNote("");
    setBusy("ingest");
    try {
      if (!docFiles.length) {
        throw new Error("Choose one or more Word, Excel, or PDF files.");
      }
      for (const docFile of docFiles) {
        assertUploadForPath(docFile, "agent");
      }
      setUploadIntent("agent");
      const gateCheck = await ensureHomepageCaptchaSuccess();
      if (!gateCheck.ok) {
        setError(gateCheck.message || CAPTCHA_FAILED_COMPLETE_MSG);
        return;
      }
      const body = new FormData();
      for (const docFile of docFiles) {
        body.append("file", docFile);
      }
      body.append("doc_category", docCategory);
      if (gateCheck.gate) {
        body.append("homepage_gate_token", gateCheck.gate);
      }
      const res = await apiFetch("/api/docs/ingest", { method: "POST", body });
      const raw = await res.text();
      let data: {
        status?: string;
        message?: string;
        captcha_ok?: boolean;
        captcha_required?: boolean;
        session_id?: string;
        filename?: string;
        filenames?: string[];
        file_count?: number;
        files?: DocSessionFile[];
        chunk_count?: number;
        preview?: string;
        doc_category?: string;
        resume_job_category?: string | null;
        category_warning?: string | null;
        char_count?: number;
        pdf_url?: string | null;
        lease_id?: string | null;
        lease_fields_extractor?: string | null;
        lease_city_index?: {
          cities?: Array<{ city?: string; lease_count?: number }>;
          cities_touched?: string[];
          preferred_city?: string;
        } | null;
      } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok || data.status === "error" || !data.session_id) {
        const msg = analyzeErrorMessage(res, raw, data);
        if (isCaptchaVerificationError(msg, data)) {
          requestHomepageCaptchaRelock();
          throw new Error(data.message || CAPTCHA_FAILED_COMPLETE_MSG);
        }
        throw new Error(msg);
      }
      setDocSession({
        session_id: data.session_id,
        filename: data.filename,
        filenames: data.filenames,
        file_count: data.file_count,
        files: data.files,
        chunk_count: data.chunk_count,
        preview: data.preview,
        message: data.message,
        doc_category: data.doc_category || docCategory,
        resume_job_category: data.resume_job_category ?? null,
        category_warning: data.category_warning ?? null,
        char_count: data.char_count,
        pdf_url: data.pdf_url ?? null,
        lease_id: data.lease_id ?? null,
        lease_fields_extractor: data.lease_fields_extractor ?? null,
      });
      setOpenStep(2);
      if (agentWorkflow === "job_search" && data.session_id) {
        startedJobSearch = data.session_id;
      }
      const cat = data.doc_category || docCategory;
      if (cat === "leases") {
        const preferred =
          (typeof data.lease_city_index?.preferred_city === "string" &&
            data.lease_city_index.preferred_city.trim()) ||
          (Array.isArray(data.lease_city_index?.cities_touched) &&
            data.lease_city_index.cities_touched.find(
              (c) => typeof c === "string" && c.trim(),
            )) ||
          "";
        const idx = data.lease_city_index?.cities;
        if (Array.isArray(idx) && idx.length) {
          const quick = idx
            .filter(
              (c) => typeof c?.city === "string" && c.city,
            )
            .map((c) => ({
              city: String(c.city),
              lease_count: Number(c.lease_count) || 0,
            }));
          if (quick.length) {
            setLeaseCities(quick);
            const match = preferred
              ? quick.find((c) => c.city === preferred)?.city
              : "";
            setLeaseCity(match || preferred || quick[0].city);
          }
        }
        void loadLeaseCities(typeof preferred === "string" ? preferred : "");
      } else {
        setLeaseCities([]);
        setLeaseCity("");
      }
    } catch (err) {
      const msg = userFacingFetchError(err, "Document ingest failed.");
      setError(msg);
      setDocSession(null);
      if (isCaptchaVerificationError(msg)) requestHomepageCaptchaRelock();
    } finally {
      setBusy(null);
    }
    if (startedJobSearch) {
      await runJobSearch(startedJobSearch);
    }
  }

  async function loadLeaseCities(preferCity?: string) {
    try {
      const res = await apiFetch("/api/docs/lease/city-folders");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        setLeaseCities([]);
        setLeaseCity("");
        return;
      }
      const cities = Array.isArray(data.cities) ? data.cities : [];
      const cleaned = cities
        .filter(
          (c: { city?: string; lease_count?: number }) =>
            typeof c?.city === "string" &&
            c.city &&
            typeof c?.lease_count === "number",
        )
        .map((c: { city: string; lease_count: number }) => ({
          city: String(c.city),
          lease_count: Number(c.lease_count) || 0,
        }));
      setLeaseCities(cleaned);
      const prefer = (preferCity || "").trim();
      setLeaseCity((prev) => {
        if (prefer && cleaned.some((c) => c.city === prefer)) {
          return prefer;
        }
        if (prev && cleaned.some((c) => c.city === prev)) {
          return prev;
        }
        return cleaned[0]?.city || "";
      });
    } catch {
      setLeaseCities([]);
      setLeaseCity("");
    }
  }

  async function onAnalyze(e: FormEvent) {
    e.preventDefault();
    if (!files.length) {
      setError(
        uploadIntent === "agent"
          ? "Choose one or more Word, Excel, or PDF files."
          : "Choose a CSV file to make an AI model.",
      );
      return;
    }
    if (uploadIntent === "agent" || files.every((f) => uploadPathForFilename(f.name) === "agent")) {
      await runDocIngest(files);
      return;
    }
    const path = uploadPathForFilename(files[0].name);
    if (path === "train") {
      await runAnalyze(files[0]);
      return;
    }
    if (path === "agent") {
      await runDocIngest(files);
      return;
    }
    setError("Unsupported file type.");
  }

  async function runLeaseTrainModel() {
    if (!docSession?.session_id) {
      setError("Upload lease files first.");
      return;
    }
    if (!leaseCity.trim()) {
      setError("Select a city/suburb before training.");
      return;
    }
    setError(null);
    setLeaseToolResult(null);
    setLeaseTrained(null);
    setLeasePrediction(null);
    setLeasePredictError(null);
    setLeasePredictInputs({});
    setLeaseTrainMeta(null);
    setLeaseSqftAnalysis(null);
    setBusy("lease");
    try {
      const res = await apiFetch("/api/docs/lease/train", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: docSession.session_id,
          auto_train: true,
          city: leaseCity.trim(),
        }),
      });
      const raw = await res.text();
      let data: {
        status?: string;
        message?: string;
        analyze?: AnalyzeResult;
        trained?: TrainResult;
        target_feature?: string;
        session_id?: string;
      } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Lease model training failed.");
      }
      const trainedPayload =
        data.trained ||
        (data.session_id
          ? { session_id: data.session_id, message: data.message }
          : null);
      if (!trainedPayload?.session_id) {
        throw new Error(data.message || "Training completed but no model session returned.");
      }
      // Stay in lease agent workflow — do not switch to Make AI models steps.
      setLeaseTrained(trainedPayload);
      setLeaseTrainMeta({
        target_feature: data.target_feature || trainedPayload.results?.target_feature,
        correlation_pair: data.analyze?.correlation_pair,
        message: data.message,
      });
      const preds = trainedPayload.results?.predictor_features || [];
      const inputs: Record<string, string> = {};
      for (const f of preds) inputs[f] = "";
      setLeasePredictInputs(inputs);
      setLeaseToolResult({
        status: "success",
        action: "train",
        message: data.message || "Model trained from lease fields.",
      });
      setOpenStep(2);
      // Also run sq ft sweep → plot + inventory on the new model.
      try {
        await fetchLeaseSqftAnalysis(trainedPayload.session_id);
      } catch (sqftErr) {
        setError(
          userFacingFetchError(
            sqftErr,
            "Model trained, but sq ft plot / inventory failed.",
          ),
        );
      }
    } catch (err) {
      setError(userFacingFetchError(err, "Lease model training failed."));
      setLeaseTrained(null);
    } finally {
      setBusy(null);
    }
  }

  /** Shared body for lease analysis component APIs (price-curve / recommend / summary). */
  function leaseAnalysisBasePayload(trainSessionId: string): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      session_id: docSession!.session_id,
      train_session_id: trainSessionId,
      city: leaseCity.trim() || undefined,
    };
    const zip = leaseZip.trim();
    if (zip !== "") payload.zip_code = zip;
    const cur = leasePopCurrent.trim();
    const proj = leasePopProjected.trim();
    if (cur !== "") payload.current_population = Number(cur);
    if (proj !== "") payload.projected_population = Number(proj);
    return payload;
  }

  async function postLeaseJson<T extends { status?: string; message?: string }>(
    path: string,
    payload: Record<string, unknown>,
    failMsg: string,
  ): Promise<T> {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const raw = await res.text();
    let data: T = {} as T;
    try {
      data = raw ? (JSON.parse(raw) as T) : ({} as T);
    } catch {
      data = {} as T;
    }
    if (!res.ok || data.status === "error") {
      throw new Error(data.message || failMsg);
    }
    return data;
  }

  /**
   * Component pipeline after train:
   *   1) /price-curve           → plot points
   *   2) /inventory-recommend   → unit mix / market share
   *   3) /analysis-summary      → display sections + headline
   */
  async function fetchLeaseSqftAnalysis(trainSessionId: string) {
    if (!docSession?.session_id) {
      throw new Error("Upload lease files first.");
    }
    const base = leaseAnalysisBasePayload(trainSessionId);

    type Inv = NonNullable<
      typeof leaseSqftAnalysis
    >["inventory_recommendation"];

    const curve = await postLeaseJson<{
      status?: string;
      message?: string;
      city?: string | null;
      points?: { sqft: number; prediction: number }[];
      flat_at_sqft?: number | null;
      sweep_start?: number;
      sweep_end?: number;
      target_feature?: string;
      predictor?: string;
      train_session_id?: string;
    }>("/api/docs/lease/price-curve", base, "Price curve failed.");

    const recommend = await postLeaseJson<{
      status?: string;
      message?: string;
      inventory_recommendation?: Inv;
      flat_at_sqft?: number | null;
      sweep_start?: number;
      sweep_end?: number;
    }>(
      "/api/docs/lease/inventory-recommend",
      {
        ...base,
        train_session_id: curve.train_session_id || trainSessionId,
        points: curve.points,
        flat_at_sqft: curve.flat_at_sqft,
        sweep_start: curve.sweep_start,
        sweep_end: curve.sweep_end,
      },
      "Inventory recommend failed.",
    );

    const summary = await postLeaseJson<{
      status?: string;
      message?: string;
      city?: string | null;
      summary?: string;
      inventory_recommendation?: Inv;
    }>(
      "/api/docs/lease/analysis-summary",
      {
        ...base,
        inventory_recommendation: recommend.inventory_recommendation,
        flat_at_sqft: curve.flat_at_sqft,
        sweep_start: curve.sweep_start,
        sweep_end: curve.sweep_end,
      },
      "Analysis summary failed.",
    );

    setLeaseSqftAnalysis({
      message: summary.message,
      city: summary.city ?? curve.city ?? null,
      curve_note: undefined,
      flat_at_sqft: curve.flat_at_sqft ?? null,
      points: curve.points || [],
      sweep_start: curve.sweep_start,
      sweep_end: curve.sweep_end,
      target_feature: curve.target_feature,
      predictor: curve.predictor,
      inventory_recommendation:
        summary.inventory_recommendation ??
        recommend.inventory_recommendation ??
        null,
    });
  }

  async function onLeasePredict(e: FormEvent) {
    e.preventDefault();
    if (!leaseTrained?.session_id) return;
    const leaseResults = leaseTrained.results;
    const leaseFeatures = leaseResults?.predictor_features || [];
    const url =
      leaseResults?.predict_url ||
      `/api/train/models/${leaseTrained.session_id}/predict`;
    setLeasePredictError(null);
    setLeasePrediction(null);
    setBusy("predict");
    try {
      const body: Record<string, number> = {};
      for (const f of leaseFeatures) {
        const raw = (leasePredictInputs[f] ?? "").trim();
        if (raw === "") {
          throw new Error(`Enter a value for ${f}.`);
        }
        const n = Number(raw);
        if (Number.isNaN(n)) {
          throw new Error(`${f} must be a number.`);
        }
        body[f] = n;
      }
      const res = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Prediction failed.");
      }
      setLeasePrediction(data as PredictResult);
    } catch (err) {
      setLeasePredictError(userFacingFetchError(err, "Prediction failed."));
    } finally {
      setBusy(null);
    }
  }

  async function runLeaseTool(
    action: "extract" | "list" | "summary" | "eval" | "delinquency" | "pay-risk",
    method: "rules" | "ollama" = "rules",
  ) {
    if (!docSession?.session_id && action !== "pay-risk") {
      setError("Upload a lease file first.");
      return;
    }
    const cityRequired =
      action === "list" ||
      action === "summary" ||
      action === "delinquency" ||
      action === "pay-risk";
    const selectedCity = leaseCity.trim();
    if (cityRequired && !selectedCity) {
      setError(
        "Select a city first. List, Delinquent, Pay Risk, and Projections use that city folder only.",
      );
      return;
    }
    setError(null);
    setLeaseToolResult(null);
    setBusy("lease");
    try {
      const payload: Record<string, string> = {};
      if (docSession?.session_id) {
        payload.session_id = docSession.session_id;
      }
      if (cityRequired) {
        payload.city = selectedCity;
      }
      if (action === "pay-risk") {
        payload.use_ollama = true;
        payload.ollama_model = "qwen3:8b";
      }
      if (action === "extract" || action === "eval") {
        payload.method = method;
        if (method === "ollama") {
          payload.ollama_model = "qwen2.5-coder:7b";
        }
      }
      const res = await apiFetch(`/api/docs/lease/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let data: LeaseToolResult & { message?: string; status?: string } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        const hint = raw.trim().startsWith("<")
          ? "The server returned a page instead of a score. Restart it so Pay risk is loaded."
          : "The server returned a score the page could not read.";
        throw new Error(hint);
      }
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || `Lease ${action} failed.`);
      }
      if (action === "pay-risk" && data.action !== "pay-risk") {
        throw new Error(data.message || "Pay risk did not return a score.");
      }
      setLeaseToolResult(data);
    } catch (err) {
      setError(userFacingFetchError(err, `Lease ${action} failed.`));
    } finally {
      setBusy(null);
    }
  }

  async function runJobSearch(sessionId?: string) {
    const sid = sessionId || docSession?.session_id;
    if (!sid) {
      setError("Upload a resume first.");
      return;
    }
    setError(null);
    setJobSearchNote("");
    setJobSearchRows(null);
    setBusy("jobs");
    try {
      const res = await apiFetch("/api/docs/resume/job-search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ session_id: sid }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Job search failed.");
      }
      const failed = Array.isArray(data.sources)
        ? data.sources
            .filter((row: { status?: string }) => row?.status === "error")
            .map((row: { source?: string }) => JOB_SOURCE_LABELS[row.source || ""] || row.source)
            .filter(Boolean)
        : [];
      setJobSearchNote(
        failed.length ? `Some boards returned no listings: ${failed.join(", ")}.` : "",
      );
      setJobSearchRows(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (err) {
      setError(userFacingFetchError(err, "Job search failed."));
      setJobSearchRows(null);
    } finally {
      setBusy(null);
    }
  }

  async function runResumeJdEval() {
    if (!docSession?.session_id) {
      setError("Upload a resume first.");
      return;
    }
    const jd = resumeJdText.trim();
    if (!jd) {
      setError("Paste a job description before scoring.");
      return;
    }
    setError(null);
    setResumeJdEval(null);
    setBusy("resume");
    try {
      const res = await apiFetch("/api/docs/resume/eval", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          session_id: docSession.session_id,
          jd_text: jd,
          use_ollama: true,
          ollama_model: "qwen3:8b",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Resume JD evaluation failed.");
      }
      setResumeJdEval(data);
    } catch (err) {
      setError(userFacingFetchError(err, "Resume JD evaluation failed."));
      setResumeJdEval(null);
    } finally {
      setBusy(null);
    }
  }

  async function useOurSample() {
    setError(null);
    setTrained(null);
    setUploadIntent("train");
    setDocSession(null);
    setBusy("sample");
    try {
      const res = await apiFetch("/api/train/sample-csv");
      const ct = res.headers.get("content-type") ?? "";
      if (!res.ok) {
        const errBody = ct.includes("json")
          ? await res.json().catch(() => ({}))
          : {};
        const detail =
          typeof errBody.error === "string"
            ? errBody.error
            : typeof errBody.message === "string"
              ? errBody.message
              : null;
        throw new Error(detail || `Could not load sample data (HTTP ${res.status}).`);
      }
      if (ct.includes("html")) {
        throw new Error("Sample data URL returned HTML instead of CSV. Redeploy the frontend build.");
      }
      const blob = await res.blob();
      const sample = new File([blob], "salesfashion_tshirts.csv", {
        type: "text/csv",
      });
      setFiles([sample]);
      await runAnalyze(sample);
    } catch (err) {
      setError(userFacingFetchError(err, "Could not use sample data."));
      setBusy(null);
    }
  }

  async function useOurSampleLeases() {
    setError(null);
    setAnalyze(null);
    setTrained(null);
    setPrediction(null);
    setLeaseToolResult(null);
    setLeaseTrained(null);
    setLeaseTrainMeta(null);
    setLeaseSqftAnalysis(null);
    setLeasePrediction(null);
    setLeasePredictError(null);
    setLeasePredictInputs({});
    setDocCategory("leases");
    setUploadIntent("agent");
    setFiles([]);
    setBusy("sample");
    try {
      // Prefer GET (same pattern as sample-csv). Avoids POST 405 on some deploys.
      let data: {
        status?: string;
        message?: string;
        session_id?: string;
        filename?: string;
        filenames?: string[];
        file_count?: number;
        files?: DocSessionFile[];
        chunk_count?: number;
        preview?: string;
        doc_category?: string;
        resume_job_category?: string | null;
        category_warning?: string | null;
        char_count?: number;
        pdf_url?: string | null;
        lease_id?: string | null;
        lease_fields_extractor?: string | null;
        lease_city_index?: {
          cities?: Array<{ city?: string; lease_count?: number }>;
          cities_touched?: string[];
          preferred_city?: string;
        } | null;
      } = {};

      const sampleRes = await apiFetch("/api/docs/sample-leases", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const sampleRaw = await sampleRes.text();
      try {
        data = sampleRaw ? JSON.parse(sampleRaw) : {};
      } catch {
        data = {};
      }

      // Fallback: city folders only (no new session endpoint required).
      if (!sampleRes.ok || data.status === "error" || !data.session_id) {
        const foldersRes = await apiFetch("/api/docs/lease/city-folders");
        const folders = await foldersRes.json().catch(() => ({}));
        if (!foldersRes.ok || folders.status === "error") {
          throw new Error(
            (typeof data.message === "string" && data.message) ||
              (typeof folders.message === "string" && folders.message) ||
              "No city folder leases available.",
          );
        }
        const cleaned = (Array.isArray(folders.cities) ? folders.cities : [])
          .filter(
            (c: { city?: string; lease_count?: number }) =>
              typeof c?.city === "string" &&
              c.city &&
              Number(c?.lease_count) > 0,
          )
          .map((c: { city: string; lease_count: number }) => ({
            city: String(c.city),
            lease_count: Number(c.lease_count) || 0,
          }));
        if (!cleaned.length) {
          throw new Error("No city folders with leases found.");
        }
        const preferred = cleaned[0].city;
        setLeaseCities(cleaned);
        setLeaseCity(preferred);
        setDocSession({
          session_id: `city-folders-${Date.now().toString(36)}`,
          filename: undefined,
          filenames: [],
          file_count: 0,
          files: [],
          chunk_count: 0,
          preview: `Using existing city folder leases (${cleaned.length} cities).`,
          message: `Using existing city folder leases. Preferred city: ${preferred}.`,
          doc_category: "leases",
          resume_job_category: null,
          category_warning: null,
        });
        setOpenStep(2);
        return;
      }

      setDocSession({
        session_id: data.session_id,
        filename: data.filename,
        filenames: data.filenames,
        file_count: data.file_count,
        files: data.files,
        chunk_count: data.chunk_count,
        preview: data.preview,
        message: data.message,
        doc_category: data.doc_category || "leases",
        resume_job_category: data.resume_job_category ?? null,
        category_warning: data.category_warning ?? null,
        char_count: data.char_count,
        pdf_url: data.pdf_url ?? null,
        lease_id: data.lease_id ?? null,
        lease_fields_extractor: data.lease_fields_extractor ?? null,
      });
      setOpenStep(2);
      const preferred =
        (typeof data.lease_city_index?.preferred_city === "string" &&
          data.lease_city_index.preferred_city.trim()) ||
        (Array.isArray(data.lease_city_index?.cities_touched) &&
          data.lease_city_index.cities_touched.find(
            (c) => typeof c === "string" && c.trim(),
          )) ||
        "";
      const idx = data.lease_city_index?.cities;
      if (Array.isArray(idx) && idx.length) {
        const quick = idx
          .filter((c) => typeof c?.city === "string" && c.city)
          .map((c) => ({
            city: String(c.city),
            lease_count: Number(c.lease_count) || 0,
          }));
        if (quick.length) {
          setLeaseCities(quick);
          const match = preferred
            ? quick.find((c) => c.city === preferred)?.city
            : "";
          setLeaseCity(match || preferred || quick[0].city);
        }
      }
      void loadLeaseCities(typeof preferred === "string" ? preferred : "");
    } catch (err) {
      setError(userFacingFetchError(err, "Could not load sample leases."));
      setDocSession(null);
    } finally {
      setBusy(null);
    }
  }

  async function onTrain(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!analyze?.session_id || !target) {
      setError("Upload an Excel or CSV and choose a target column first.");
      return;
    }
    setBusy("train");
    try {
      const res = await apiFetch("/api/train/run", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ session_id: analyze.session_id, target_feature: target }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Training failed.");
      }
      setTrained(data as TrainResult);
      setOpenStep(3);
    } catch (err) {
      setError(userFacingFetchError(err, "Training failed."));
      setTrained(null);
    } finally {
      setBusy(null);
    }
  }

  async function onPredict(e: FormEvent) {
    e.preventDefault();
    if (!trained?.session_id || !ready) return;
    setPredictError(null);
    setPrediction(null);
    setBusy("predict");
    try {
      const body: Record<string, number> = {};
      for (const f of features) {
        const raw = (predictInputs[f] ?? "").trim();
        if (raw === "") {
          throw new Error(`Enter a value for ${f}.`);
        }
        const n = Number(raw);
        if (Number.isNaN(n)) {
          throw new Error(`${f} must be a number.`);
        }
        body[f] = n;
      }
      const res = await apiFetch(predictUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || "Prediction failed.");
      }
      setPrediction(data as PredictResult);
    } catch (err) {
      setPredictError(userFacingFetchError(err, "Prediction failed."));
    } finally {
      setBusy(null);
    }
  }

  async function onScheduleConsultation() {
    setCalendlyError(null);
    try {
      const res = await apiFetch("/api/train/calendly-config", {
        headers: { Accept: "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      const url =
        typeof data.scheduling_url === "string" ? data.scheduling_url.trim() : "";
      if (!res.ok || !data.enabled || !url) {
        throw new Error(
          data.message ||
            "Consultation booking is not configured (need CALENDLY_MEETING_URL and CALENDLY_MEETING_TOKEN).",
        );
      }
      const { openCalendlyPopup } = await import("../lib/calendlyPopup");
      await openCalendlyPopup(url, {
        name: buyerName.trim(),
        email: buyerEmail.trim(),
      });
    } catch (err) {
      setCalendlyError(
        userFacingFetchError(err, "Could not open consultation booking."),
      );
    }
  }

  const targetOptions: RecommendedTarget[] =
    analyze?.recommended_targets && analyze.recommended_targets.length
      ? analyze.recommended_targets
      : (analyze?.preview?.columns || []).map((name) => ({ name }));

  const analyzing = busy === "analyze" || busy === "sample" || busy === "ingest";
  const csvId = compact ? "home-train-csv" : "train-csv";
  const targetId = compact ? "home-train-target" : "train-target";
  const tone = summary?.tone || (ready ? "good" : "warn");
  const agentMode = Boolean(docSession && !analyze);
  const filePath = file ? uploadPathForFilename(file.name) : null;
  const showModelAction = filePath === "train";
  const showAgentAction = filePath === "agent";
  /** Steps 3–4 are Make AI models only — hide on Ask AI about a file. */
  const showModelOnlySteps = uploadIntent === "train";
  const fileAccept =
    uploadIntent === "agent"
      ? ".pdf,.docx,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : ".csv,text/csv";

  function chooseUploadIntent(next: UploadPath) {
    if (pathLocked || busy !== null || next === uploadIntent) return;
    setUploadIntent(next);
    setFiles([]);
    setError(null);
    setAnalyze(null);
    setDocSession(null);
    setTrained(null);
    setPrediction(null);
    setLeaseToolResult(null);
    setLeaseTrained(null);
    setLeaseTrainMeta(null);
    setLeaseSqftAnalysis(null);
    setLeaseCities([]);
    setLeaseCity("");
    setLeasePrediction(null);
    setLeasePredictError(null);
    setLeasePredictInputs({});
    if (next === "agent" && (openStep === 3 || openStep === 4)) {
      setOpenStep(1);
    }
    const input = document.getElementById(csvId) as HTMLInputElement | null;
    if (input) input.value = "";
  }

  return (
    <div className={`train-model-panel${compact ? " train-model-panel--compact" : ""}`}>
      <div
        className="train-guide"
        role="tablist"
        aria-label={
          lockedPath === "agent"
            ? "Ask AI about a file"
            : lockedPath === "train"
              ? "Make AI models"
              : "Make AI models or ask AI about a file"
        }
      >
        <section className={`train-guide-step${openStep === 1 ? " train-guide-step--open" : ""}`}>
          <div className="train-guide-tab-row">
            <button
              type="button"
              className="train-guide-tab"
              role="tab"
              aria-expanded={openStep === 1}
              aria-controls="train-guide-panel-1"
              id="train-guide-tab-1"
              onClick={() => setOpenStep(1)}
            >
              <span className="train-guide-num">1.</span>{" "}
              {lockedPath === "agent"
                ? "Upload your file"
                : lockedPath === "train"
                  ? "Upload your CSV data file"
                  : "Choose what you want to do"}
            </button>
            <button
              type="button"
              className="linkish train-example-data-link"
              disabled={busy !== null || uploadIntent !== "train"}
              onClick={() => setSampleDataModalOpen(true)}
            >
              {/*See example sales data*/}
            </button>
          </div>
          {openStep === 1 && (
            <div
              className="train-guide-panel"
              role="tabpanel"
              id="train-guide-panel-1"
              aria-labelledby="train-guide-tab-1"
            >
              {!pathLocked && (
              <div
                className="train-path-chooser"
                role="group"
                aria-label="Choose Make AI models or Ask AI about a file"
              >
                <button
                  type="button"
                  className={`train-path-btn${uploadIntent === "train" ? " train-path-btn--active" : ""}`}
                  aria-pressed={uploadIntent === "train"}
                  disabled={busy !== null}
                  onClick={() => chooseUploadIntent("train")}
                >
                  <span className="train-path-btn-title">Make AI models</span>
                  <span className="train-path-btn-hint">Use a CSV spreadsheet to predict numbers</span>
                </button>
                <button
                  type="button"
                  className={`train-path-btn${uploadIntent === "agent" ? " train-path-btn--active" : ""}`}
                  aria-pressed={uploadIntent === "agent"}
                  disabled={busy !== null}
                  onClick={() => chooseUploadIntent("agent")}
                >
                  <span className="train-path-btn-title">Ask AI about a file</span>
                  <span className="train-path-btn-hint">Word, Excel, or PDF — get answers in plain English</span>
                </button>
              </div>
              )}

              <form className="deposit-form buy-server-form" onSubmit={onAnalyze}>
                {uploadIntent === "agent" && (
                  <div className="buy-server-row train-doc-category-row">
                    <label htmlFor="train-doc-category">File category</label>
                    <select
                      id="train-doc-category"
                      className="train-doc-category-select"
                      value={agentWorkflow}
                      disabled={busy !== null}
                      onChange={(ev) => {
                        const next = ev.target.value;
                        if (next === "job_search") {
                          setAgentWorkflow("job_search");
                          setDocCategory("resumes");
                          return;
                        }
                        const category = next === "leases" ? "leases" : "resumes";
                        setAgentWorkflow(category);
                        setDocCategory(category);
                      }}
                      aria-label="Choose leases, resumes, or job search"
                    >
                      <option value="leases">Leases — rental / lease agreements</option>
                      <option value="resumes">Resumes — CV / resume documents</option>
                      <option value="job_search">Job Search — resume vs job posts</option>
                    </select>
                  </div>
                )}
                <div className="buy-server-row">
                  <span className="train-upload-label-spacer" aria-hidden="true" />
                  <div className="buy-server-control">
                    <input
                      id={csvId}
                      key={`upload-${uploadIntent}`}
                      type="file"
                      multiple={uploadIntent === "agent"}
                      aria-label={
                        uploadIntent === "agent"
                          ? "Upload one or more Word, Excel, or PDF files for AI analysis"
                          : "Upload CSV to make an AI model"
                      }
                      accept={fileAccept}
                      onChange={(ev) => {
                        const list = ev.target.files
                          ? Array.from(ev.target.files)
                          : [];
                        if (!list.length) {
                          setFiles([]);
                          return;
                        }
                        try {
                          if (uploadIntent === "train") {
                            const next = list[0];
                            assertUploadForPath(next, "train");
                            setError(null);
                            setFiles([next]);
                            return;
                          }
                          for (const next of list) {
                            assertUploadForPath(next, "agent");
                          }
                          const detected = uploadPathForFilename(list[0].name);
                          if (detected) setUploadIntent(detected);
                          setError(null);
                          setFiles(list);
                        } catch (err) {
                          setFiles([]);
                          ev.target.value = "";
                          setError(
                            err instanceof Error
                              ? err.message
                              : "Please choose a supported file.",
                          );
                        }
                      }}
                      disabled={busy !== null}
                    />
                    <p className="buy-server-product-note train-sample-link">
                      {uploadIntent === "agent"
                        ? "Accepted: Word (.docx), Excel (.xlsx / .xls), or PDF. Multiple files OK (max 100, 1 MB each)."
                        : "Accepted: CSV (.csv) only. Max 1 MB."}
                    </p>
                    {uploadIntent === "agent" && files.length > 0 ? (
                      <p className="buy-server-product-note train-sample-link">
                        Selected: {files.map((f) => f.name).join(", ")}
                      </p>
                    ) : null}
                    {uploadIntent === "agent" && docCategory === "leases" && (
                      <p className="buy-server-product-note train-sample-link">
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy !== null}
                          onClick={() => void useOurSampleLeases()}
                        >
                          {busy === "sample" || busy === "ingest"
                            ? "Using our sample leases…"
                            : <>
                                No leases?{" "}
                                Click here, use our Sample leases
                              </>}
                        </button>
                      </p>
                    )}
                    {uploadIntent === "train" && (
                      <p className="buy-server-product-note train-sample-link">
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy !== null}
                          onClick={() => void useOurSample()}
                        >
                          {busy === "sample" || busy === "analyze"
                            ? "Using our sample…"
                            : <>
                                No data?{" "}
                                Click here to use T-shirt sales sample!
                              </>}
                        </button>
                      </p>
                    )}
                  </div>
                </div>
                <div className="buy-server-actions">
                  {showModelAction && (
                    <button type="submit" className="btn btn-primary" disabled={busy !== null}>
                      {analyzing && busy !== "ingest" ? "Analyzing…" : "Upload & analyze"}
                    </button>
                  )}
                  {showAgentAction && (
                    <button type="submit" className="btn btn-primary" disabled={busy !== null}>
                      {busy === "ingest"
                        ? files.length > 1
                          ? `Reading ${files.length} files…`
                          : "Reading your file…"
                        : files.length > 1
                          ? `AI agent — analyze ${files.length} files`
                          : "AI agent — analyze file"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </section>

        <section className={`train-guide-step${openStep === 2 ? " train-guide-step--open" : ""}`}>
          <button
            type="button"
            className="train-guide-tab"
            role="tab"
            aria-expanded={openStep === 2}
            aria-controls="train-guide-panel-2"
            id="train-guide-tab-2"
            onClick={() => setOpenStep(2)}
            disabled={!analyze && !docSession}
          >
            <span className="train-guide-num">2.</span>{" "}
            {agentMode ? "Ask questions about your file" : "Choose what to predict"}
          </button>
          {openStep === 2 && (
            <div
              className="train-guide-panel"
              role="tabpanel"
              id="train-guide-panel-2"
              aria-labelledby="train-guide-tab-2"
            >
              {agentMode && docSession ? (
                <div
                  className={`deposit-form buy-server-form train-step${
                    docSession.doc_category === "leases" ||
                    agentWorkflow === "job_search" ||
                    agentWorkflow === "resumes"
                      ? " buy-server-form--lease-full"
                      : ""
                  }`}
                >
                  {docSession.doc_category === "leases" ? (
                    <p className="buy-server-product-note" style={{ marginTop: 0 }}>
                      <strong>Problem Statement:</strong>{" "}
                      {leaseToolResult?.action === "delinquency"
                        ? LEASE_PROBLEM_DELINQUENCY
                        : LEASE_PROBLEM_UNIT_SIZE}
                    </p>
                  ) : null}
                  <p className="buy-server-product-note">
                    Ready:{" "}
                    <strong>
                      {(docSession.file_count ||
                        docSession.files?.length ||
                        docSession.filenames?.length ||
                        0) > 1
                        ? `${
                            docSession.file_count ||
                            docSession.files?.length ||
                            docSession.filenames?.length
                          } files`
                        : docSession.filename || "your file"}
                    </strong>
                    {typeof docSession.chunk_count === "number"
                      ? ` (${docSession.chunk_count} section${
                          docSession.chunk_count === 1 ? "" : "s"
                        } indexed)`
                      : ""}
                    {" · analyzing"}
                  </p>
                  {docSession.category_warning ? (
                    <p className="model-run-error" role="status">
                      {docSession.category_warning}
                    </p>
                  ) : null}

                  {docSession.doc_category === "leases" ? (
                    <div className="lease-session-tools" style={{ marginBottom: "1.25rem" }}>
                      <div className="buy-server-row lease-search-row" style={{ marginBottom: "0.75rem" }}>
                        <label htmlFor="lease-city-select">City</label>
                        <select
                          id="lease-city-select"
                          className="train-doc-category-select"
                          value={leaseCity}
                          disabled={busy !== null || leaseCities.length === 0}
                          onChange={(ev) => {
                            setLeaseCity(ev.target.value);
                            setLeaseTrained(null);
                            setLeaseTrainMeta(null);
                            setLeaseSqftAnalysis(null);
                            setLeasePrediction(null);
                            setLeasePredictError(null);
                            setLeasePredictInputs({});
                            setLeaseToolResult(null);
                          }}
                          aria-label="Select city by folder file count"
                        >
                          {leaseCities.length === 0 ? (
                            <option value="">No city folders yet</option>
                          ) : (
                            leaseCities.map((c) => (
                              <option key={c.city} value={c.city}>
                                {c.city} · {c.lease_count} lease
                                {c.lease_count === 1 ? "" : "s"}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                      <div className="lease-tool-actions" role="group" aria-label="Lease tools">
                        <button
                          type="button"
                          className={`btn btn-primary${
                            leaseToolResult?.action === "list"
                              ? " lease-tool-btn--active"
                              : ""
                          }`}
                          disabled={busy !== null || !leaseCity.trim()}
                          onClick={() => void runLeaseTool("list")}
                          title={
                            leaseCity.trim()
                              ? `List files in ${leaseCity} city folder`
                              : "Select a city first"
                          }
                        >
                          {busy === "lease" ? "Working…" : "List"}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-primary${
                            leaseToolResult?.action === "delinquency"
                              ? " lease-tool-btn--active"
                              : ""
                          }`}
                          disabled={busy !== null || !leaseCity.trim()}
                          aria-pressed={leaseToolResult?.action === "delinquency"}
                          onClick={() => {
                            if (leaseToolResult?.action === "delinquency") {
                              void runLeaseTool("list");
                            } else {
                              void runLeaseTool("delinquency");
                            }
                          }}
                          title={
                            leaseCity.trim()
                              ? leaseToolResult?.action === "delinquency"
                                ? `Clear delinquent filter — show all ${leaseCity} leases`
                                : `Show delinquent leases in ${leaseCity} (most overdue first)`
                              : "Select a city first"
                          }
                        >
                          Delinquent
                        </button>
                        <button
                          type="button"
                          className={`btn btn-primary${
                            leaseToolResult?.action === "pay-risk"
                              ? " lease-tool-btn--active"
                              : ""
                          }`}
                          disabled={busy !== null || !leaseCity.trim()}
                          onClick={() => void runLeaseTool("pay-risk")}
                          title={
                            leaseCity.trim()
                              ? `Score leases in ${leaseCity} for cash surplus, rent stretch, and credit risk`
                              : "Select a city first"
                          }
                        >
                          {busy === "lease" ? "Working…" : "Pay Risk"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busy !== null || !leaseCity.trim()}
                          onClick={() => void runLeaseTrainModel()}
                          title={
                            leaseCity.trim()
                              ? `Project unit size and price from ${leaseCity} leases`
                              : "Select a city first"
                          }
                        >
                          {busy === "lease" ? "Working…" : "Projections"}
                        </button>
                      </div>
                      {error ? (
                        <p className="model-run-error" role="alert" style={{ marginTop: "0.75rem" }}>
                          {error}
                        </p>
                      ) : null}
                      {leaseToolResult ? (
                        <div className="train-verdict train-verdict--good" style={{ marginTop: "1rem" }}>
                          {leaseToolResult.action === "list" ||
                          leaseToolResult.action === "delinquency" ? null : (
                            <h3>
                              {leaseToolResult.action
                                ? leaseToolResult.action.charAt(0).toUpperCase() +
                                  leaseToolResult.action.slice(1)
                                : "Lease result"}
                              {leaseToolResult.method
                                ? ` · ${leaseToolResult.method}`
                                : ""}
                              {typeof leaseToolResult.file_count === "number" &&
                              leaseToolResult.file_count > 1
                                ? ` · ${leaseToolResult.file_count} files`
                                : ""}
                            </h3>
                          )}
                          {leaseToolResult.action === "list" ||
                          leaseToolResult.action === "delinquency"
                            ? null
                            : leaseToolResult.message
                              ? <p>{leaseToolResult.message}</p>
                              : null}
                          {typeof leaseToolResult.accuracy === "number" ? (
                            <p>
                              Accuracy: {leaseToolResult.matches}/{leaseToolResult.total_fields}{" "}
                              ({(leaseToolResult.accuracy * 100).toFixed(1)}%)
                            </p>
                          ) : null}
                          {leaseToolResult.table &&
                          (leaseToolResult.table.columns?.length || 0) > 0 ? (
                            <LeaseToolResultsTable
                              table={leaseToolResult.table}
                              city={leaseToolResult.city || leaseCity}
                              action={leaseToolResult.action}
                              delinquentCount={leaseToolResult.delinquent_count}
                              totalBalanceOwed={leaseToolResult.total_balance_owed}
                              totalLeases={leaseToolResult.total_leases}
                            />
                          ) : leaseToolResult.formatted ? (
                            <p
                              style={{
                                whiteSpace: "pre-wrap",
                                fontFamily: "ui-monospace, monospace",
                              }}
                            >
                              {leaseToolResult.formatted}
                            </p>
                          ) : null}
                          {leaseToolResult.llm_evaluation ? (
                            <div className="buy-server-product-note" role="status">
                              <p>
                                <strong>Ollama</strong>
                                {leaseToolResult.llm_evaluation.model
                                  ? ` (${leaseToolResult.llm_evaluation.model})`
                                  : ""}
                                {leaseToolResult.llm_evaluation.mode
                                  ? ` · ${leaseToolResult.llm_evaluation.mode}`
                                  : ""}
                                {leaseToolResult.llm_evaluation.status
                                  ? ` · ${leaseToolResult.llm_evaluation.status}`
                                  : ""}
                              </p>
                              {leaseToolResult.llm_evaluation.status === "success" ? (
                                <>
                                  {(
                                    leaseToolResult.llm_evaluation.portfolio_summary ||
                                    leaseToolResult.llm_evaluation.narrative
                                  ) ? (
                                    <p>
                                      {leaseToolResult.llm_evaluation.portfolio_summary ||
                                        leaseToolResult.llm_evaluation.narrative}
                                    </p>
                                  ) : null}
                                  {(leaseToolResult.llm_evaluation.risk_themes?.length || 0) >
                                  0 ? (
                                    <ul>
                                      {leaseToolResult.llm_evaluation.risk_themes!.slice(0, 4).map(
                                        (t) => (
                                          <li key={t}>{t}</li>
                                        ),
                                      )}
                                    </ul>
                                  ) : null}
                                </>
                              ) : (
                                <p>
                                  {leaseToolResult.llm_evaluation.message ||
                                    leaseToolResult.llm_evaluation.reason ||
                                    "No Ollama response."}
                                </p>
                              )}
                            </div>
                          ) : null}
                          {(leaseToolResult.table?.rows?.length || 0) <= 1 &&
                            (leaseToolResult.hit_pdf_url ||
                              leaseToolResult.pdf_url ||
                              leaseToolResult.pdf_download ||
                              leaseToolResult.corpus_pdf_url) && (
                            <p style={{ marginTop: "0.75rem" }}>
                              Source PDF:{" "}
                              <a
                                href={
                                  leaseToolResult.hit_pdf_url ||
                                  leaseToolResult.pdf_url ||
                                  leaseToolResult.pdf_download ||
                                  leaseToolResult.corpus_pdf_url ||
                                  "#"
                                }
                                download={
                                  leaseToolResult.pdf_filename ||
                                  docSession.filename ||
                                  "lease.pdf"
                                }
                              >
                                Download{" "}
                                {leaseToolResult.pdf_filename ||
                                  docSession.filename ||
                                  "lease.pdf"}
                              </a>
                              {leaseToolResult.corpus_pdf_url &&
                              leaseToolResult.corpus_pdf_url !==
                                (leaseToolResult.pdf_url ||
                                  leaseToolResult.pdf_download) ? (
                                <>
                                  {" · "}
                                  <a
                                    href={leaseToolResult.corpus_pdf_url}
                                    download={
                                      leaseToolResult.pdf_filename || "lease.pdf"
                                    }
                                  >
                                    Corpus copy
                                  </a>
                                </>
                              ) : null}
                            </p>
                          )}
                        </div>
                      ) : null}
                      {leaseTrained ? (
                        <div style={{ marginTop: "1rem" }}>
                          {leaseSqftAnalysis ? (
                            <div
                              className="lease-sqft-report"
                              style={{ marginTop: "0.75rem" }}
                            >
                              {(leaseSqftAnalysis.points?.length || 0) > 1 ? (
                                <LeaseSqftPricePlot
                                  points={leaseSqftAnalysis.points || []}
                                  flatAt={leaseSqftAnalysis.flat_at_sqft}
                                />
                              ) : null}
                              <div
                                className="train-verdict train-verdict--good"
                                style={{ marginTop: "0.85rem" }}
                              >
                                <h3>
                                  {leaseSqftAnalysis.city
                                    ? `${leaseSqftAnalysis.city} — optimal size & inventory`
                                    : "Optimal size & inventory"}
                                </h3>
                                {(() => {
                                  const headline =
                                    leaseOptimalSizeHeadline(
                                      leaseSqftAnalysis.inventory_recommendation,
                                    ) ||
                                    (leaseSqftAnalysis.message || "")
                                      .replace(/\bplateau\b/gi, "Optimal calculated size")
                                      .trim() ||
                                    null;
                                  return headline ? (
                                    <p className="lease-sqft-report__headline">
                                      {headline}
                                    </p>
                                  ) : null;
                                })()}
                                {leaseSqftAnalysis.inventory_recommendation
                                  ?.demand_metrics ? (
                                  <div className="lease-demand-metrics">
                                    <h4>Site demand profile — calculated</h4>
                                    <dl className="lease-demand-metrics__grid">
                                      <div>
                                        <dt>Density</dt>
                                        <dd>
                                          {leaseSqftAnalysis.inventory_recommendation
                                            .demand_metrics
                                            .population_density_per_sq_mi != null
                                            ? `${Number(
                                                leaseSqftAnalysis
                                                  .inventory_recommendation
                                                  .demand_metrics
                                                  .population_density_per_sq_mi,
                                              ).toLocaleString(undefined, {
                                                maximumFractionDigits: 0,
                                              })} / sq mi`
                                            : "—"}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt>Churn</dt>
                                        <dd>
                                          {leaseSqftAnalysis.inventory_recommendation
                                            .demand_metrics.churn_rate_pct != null
                                            ? `${Number(
                                                leaseSqftAnalysis
                                                  .inventory_recommendation
                                                  .demand_metrics.churn_rate_pct,
                                              ).toFixed(0)}%`
                                            : "—"}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt>Renter</dt>
                                        <dd>
                                          {leaseSqftAnalysis.inventory_recommendation
                                            .demand_metrics.renter_ratio_pct != null
                                            ? `${Number(
                                                leaseSqftAnalysis
                                                  .inventory_recommendation
                                                  .demand_metrics.renter_ratio_pct,
                                              ).toFixed(0)}%`
                                            : "—"}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt>Multiplier</dt>
                                        <dd>
                                          {leaseSqftAnalysis.inventory_recommendation
                                            .demand_metrics.demand_multiplier != null
                                            ? `×${Number(
                                                leaseSqftAnalysis
                                                  .inventory_recommendation
                                                  .demand_metrics.demand_multiplier,
                                              ).toFixed(2)}`
                                            : "—"}
                                        </dd>
                                      </div>
                                    </dl>
                                    {(leaseSqftAnalysis.inventory_recommendation
                                      .demand_metrics.takeaways?.length || 0) > 0 ? (
                                      <ul className="lease-demand-metrics__takeaways">
                                        {leaseSqftAnalysis.inventory_recommendation.demand_metrics.takeaways!.map(
                                          (t) => (
                                            <li key={t}>{t}</li>
                                          ),
                                        )}
                                      </ul>
                                    ) : null}
                                  </div>
                                ) : null}
                                {(leaseSqftAnalysis.inventory_recommendation
                                  ?.summary_sections?.length || 0) > 0 ? (
                                  <div className="lease-sqft-report__sections">
                                    {leaseSqftAnalysis.inventory_recommendation!.summary_sections!.map(
                                      (sec) =>
                                        sec.title === "Site demand profile" ? null : (
                                          <div
                                            className="lease-sqft-report__section"
                                            key={sec.title || sec.items?.[0]}
                                          >
                                            {sec.title ? <h4>{sec.title}</h4> : null}
                                            <ul>
                                              {(sec.items || []).map((item) => (
                                                <li key={item}>{item}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        ),
                                    )}
                                  </div>
                                ) : (
                                  <p className="buy-server-product-note" role="status">
                                    Re-run analysis to refresh the structured report
                                    (price plateau, historic sample, demand, inventory).
                                  </p>
                                )}
                                {(leaseSqftAnalysis.inventory_recommendation
                                  ?.recommended_mix?.length || 0) > 0 ? (
                                  <div className="lease-results-table-wrap">
                                    <table className="lease-results-table">
                                      <thead>
                                        <tr>
                                          <th>Size band</th>
                                          <th>Recommended units</th>
                                          <th>Historic share</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {leaseSqftAnalysis.inventory_recommendation!.recommended_mix!.map(
                                          (row) => (
                                            <tr key={row.band || row.label}>
                                              <td>{row.label || row.band}</td>
                                              <td>{row.recommended_units ?? "—"}</td>
                                              <td>
                                                {row.historic_share != null
                                                  ? `${(row.historic_share * 100).toFixed(0)}%`
                                                  : "—"}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : null}
                                {leaseSqftAnalysis.inventory_recommendation?.demand_metrics
                                  ?.profile_url ? (
                                  <p className="buy-server-product-note">
                                    Source:{" "}
                                    <a
                                      href={
                                        leaseSqftAnalysis.inventory_recommendation
                                          .demand_metrics.profile_url
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Census Reporter profile
                                    </a>
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                          {leaseReady && leaseFeatures.length > 0 ? (
                            <form
                              className="deposit-form buy-server-form train-predict-form"
                              onSubmit={onLeasePredict}
                              style={{ marginTop: "0.75rem" }}
                            >
                              <div className="train-predict-row">
                                {leaseFeatures.map((f) => (
                                  <div className="train-predict-field" key={f}>
                                    <label htmlFor={`lease-pred-${compact ? "c-" : ""}${f}`}>
                                      {f}
                                    </label>
                                    <input
                                      id={`lease-pred-${compact ? "c-" : ""}${f}`}
                                      type="number"
                                      step="any"
                                      required
                                      value={leasePredictInputs[f] ?? ""}
                                      onChange={(ev) =>
                                        setLeasePredictInputs((prev) => ({
                                          ...prev,
                                          [f]: ev.target.value,
                                        }))
                                      }
                                      placeholder="1"
                                      disabled={busy !== null}
                                      className="train-predict-input"
                                    />
                                  </div>
                                ))}
                                <button
                                  type="submit"
                                  className="btn btn-primary train-predict-submit"
                                  disabled={busy !== null}
                                >
                                  {busy === "predict" ? "Predicting…" : "Get lease prediction"}
                                </button>
                              </div>
                              {leasePredictError ? (
                                <p className="model-run-error" role="alert">
                                  {leasePredictError}
                                </p>
                              ) : null}
                              {leasePrediction ? (
                                <div className="deposit-success train-predict-result">
                                  <p className="train-predict-result-text">
                                    {leasePrediction.interpretation || (
                                      <>
                                        Predicted{" "}
                                        <strong>
                                          {leasePrediction.target_feature ||
                                            leaseTrainMeta?.target_feature}
                                        </strong>
                                        :{" "}
                                        <strong>
                                          {leasePrediction.prediction != null
                                            ? leasePrediction.prediction.toFixed(2)
                                            : "—"}
                                        </strong>
                                      </>
                                    )}
                                  </p>
                                </div>
                              ) : null}
                            </form>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {agentWorkflow === "job_search" &&
                  docSession.doc_category === "resumes" ? (
                    <div
                      className="lease-session-tools"
                      style={{ marginBottom: "1.25rem" }}
                    >
                      <p className="buy-server-product-note" style={{ marginTop: 0 }}>
                        Upload is your resume. Search checks Remote OK, Craigslist,
                        SimplyHired, Wellfound, Geebo, and Google Jobs, then shows the
                        10 highest resume matches.
                      </p>
                      <div className="buy-server-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busy !== null}
                          onClick={() => void runJobSearch()}
                        >
                          {busy === "jobs" ? "Scoring job posts…" : "Find top 10 matches"}
                        </button>
                      </div>
                      {jobSearchNote ? (
                        <p className="buy-server-product-note" role="status">
                          {jobSearchNote}
                        </p>
                      ) : null}
                      {jobSearchRows ? (
                        <div className="lease-results-table-wrap job-search-results">
                          <table className="lease-results-table">
                            <thead>
                              <tr>
                                <th>Rank</th>
                                <th>Job</th>
                                <th>Board</th>
                                <th>Location</th>
                                <th>Match</th>
                                <th>Recommendation</th>
                                <th>Link</th>
                              </tr>
                            </thead>
                            <tbody>
                              {jobSearchRows.length === 0 ? (
                                <tr>
                                  <td colSpan={7}>No scored job posts.</td>
                                </tr>
                              ) : (
                                jobSearchRows.map((job, index) => (
                                  <tr key={job.url || job.title || index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      {job.title || "—"}
                                      {job.company ? ` · ${job.company}` : ""}
                                    </td>
                                    <td>{JOB_SOURCE_LABELS[job.source || ""] || job.source || "—"}</td>
                                    <td>{job.location || "—"}</td>
                                    <td>
                                      {job.match_pct != null ? `${job.match_pct}%` : "—"}
                                    </td>
                                    <td>{job.recommendation || "—"}</td>
                                    <td>
                                      {job.url ? (
                                        <a href={job.url} target="_blank" rel="noreferrer">
                                          Open
                                        </a>
                                      ) : (
                                        "—"
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {agentWorkflow === "resumes" && docSession.doc_category === "resumes" ? (
                    <div
                      className="lease-session-tools"
                      style={{ marginBottom: "1.25rem" }}
                    >
                      {docSession.resume_job_category ? (
                        <p className="buy-server-product-note" style={{ marginTop: 0 }}>
                          Screening category:{" "}
                          <strong>{docSession.resume_job_category}</strong>
                        </p>
                      ) : null}
                      <div className="buy-server-row resume-jd-row">
                        <label htmlFor="resume-jd-text">Job description</label>
                        <textarea
                          id="resume-jd-text"
                          rows={6}
                          value={resumeJdText}
                          onChange={(ev) => setResumeJdText(ev.target.value)}
                          placeholder="Paste the job description to score this resume against…"
                          disabled={busy !== null}
                        />
                      </div>
                      <div className="buy-server-actions resume-jd-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busy !== null || !resumeJdText.trim()}
                          onClick={() => void runResumeJdEval()}
                        >
                          {busy === "resume"
                            ? "Matching resume…"
                            : "Match Resume to Job"}
                        </button>
                      </div>
                      {resumeJdEval ? (
                        <div
                          className="train-verdict train-verdict--good"
                          style={{ marginTop: "1rem" }}
                        >
                          <h3>
                            {resumeJdEval.match_label || resumeJdEval.recommendation || "JD match"} ·{" "}
                            {resumeJdEval.overall_match_pct != null
                              ? `${resumeJdEval.overall_match_pct}%`
                              : "—"}
                          </h3>
                          <p className="buy-server-product-note">
                            Candidate:{" "}
                            {resumeJdEval.candidate || "Candidate"}
                            {resumeJdEval.role
                              ? ` · Role: ${
                                  resumeJdEval.role.length > 72
                                    ? `${resumeJdEval.role.slice(0, 72)}…`
                                    : resumeJdEval.role
                                }`
                              : ""}
                            {resumeJdEval.recommendation
                              ? ` · ${resumeJdEval.recommendation}`
                              : ""}
                            {" · "}
                            Mandatory:{" "}
                            {resumeJdEval.mandatory_met ? "met" : "not met"}
                            {resumeJdEval.evidence_coverage_pct != null
                              ? ` · Evidence coverage ${resumeJdEval.evidence_coverage_pct}%`
                              : ""}
                          </p>
                          {resumeJdEval.match_label === "Potential Match" &&
                          resumeJdEval.recommendation === "Improve Resume" ? (
                            <p className="buy-server-product-note">
                              Solid evidence; below the 70% Apply Now bar —
                              strengthen the résumé, then re-run.
                            </p>
                          ) : null}
                          {(resumeJdEval.overall_match_pct ?? 0) >= 99.5 ? (
                            <p className="buy-server-product-note" role="status">
                              Suspect perfect match — spot-check evidence; 100%
                              fit is rare under evidence-only scoring.
                            </p>
                          ) : null}
                          <p className="buy-server-product-note">
                            Evaluation quality:{" "}
                            {resumeJdEval.evaluation_quality_pct != null
                              ? `${resumeJdEval.evaluation_quality_pct}%`
                              : "—"}
                            {resumeJdEval.evaluation_quality_label
                              ? ` · ${resumeJdEval.evaluation_quality_label}`
                              : ""}
                            {resumeJdEval.harness_intervene
                              ? " · Harness intervening"
                              : ""}
                          </p>
                          {resumeJdEval.harness_intervene &&
                          (resumeJdEval.harness_intervention?.actions?.length || 0) > 0 ? (
                            <div className="buy-server-product-note" role="status">
                              <p>
                                <strong>Harness:</strong> improve inputs, then re-run
                                Match Resume to Job.
                              </p>
                              <ul>
                                {resumeJdEval.harness_intervention!.actions!.slice(0, 4).map(
                                  (action) => (
                                    <li key={action}>{action}</li>
                                  ),
                                )}
                              </ul>
                            </div>
                          ) : null}
                          {resumeJdEval.llm_evaluation ? (
                            <div className="buy-server-product-note" role="status">
                              <p>
                                <strong>Ollama</strong>
                                {resumeJdEval.llm_evaluation.model
                                  ? ` (${resumeJdEval.llm_evaluation.model})`
                                  : ""}
                                {resumeJdEval.llm_evaluation.mode
                                  ? ` · ${resumeJdEval.llm_evaluation.mode}`
                                  : ""}
                                {resumeJdEval.llm_evaluation.status
                                  ? ` · ${resumeJdEval.llm_evaluation.status}`
                                  : ""}
                                {resumeJdEval.llm_evaluation.skill_fit_pct != null
                                  ? ` · Skill fit ${resumeJdEval.llm_evaluation.skill_fit_pct}%`
                                  : ""}
                                {resumeJdEval.llm_evaluation.team_fit_pct != null
                                  ? ` · Team fit ${resumeJdEval.llm_evaluation.team_fit_pct}%`
                                  : ""}
                              </p>
                              {resumeJdEval.llm_evaluation.status === "success" ? (
                                <>
                                  {resumeJdEval.llm_evaluation.narrative ? (
                                    <p>{resumeJdEval.llm_evaluation.narrative}</p>
                                  ) : null}
                                  {resumeJdEval.llm_evaluation.skill_fit_note ? (
                                    <p>Skill: {resumeJdEval.llm_evaluation.skill_fit_note}</p>
                                  ) : null}
                                  {resumeJdEval.llm_evaluation.team_fit_note ? (
                                    <p>Team: {resumeJdEval.llm_evaluation.team_fit_note}</p>
                                  ) : null}
                                </>
                              ) : (
                                <p>
                                  {resumeJdEval.llm_evaluation.message ||
                                    resumeJdEval.llm_evaluation.reason ||
                                    "No Ollama response."}
                                </p>
                              )}
                            </div>
                          ) : null}
                          {resumeJdEval.career_track?.note ? (
                            <p className="buy-server-product-note">
                              {resumeJdEval.career_track.note}
                            </p>
                          ) : null}
                          {resumeJdEval.screening_category ? (
                            <p className="buy-server-product-note">
                              Resume model: <strong>{resumeJdEval.screening_category}</strong>
                              {resumeJdEval.screening_confidence
                                ? ` (${Math.round(resumeJdEval.screening_confidence * 100)}%)`
                                : ""}
                            </p>
                          ) : null}
                          {resumeJdEval.jd_role ? (
                            <p className="buy-server-product-note">
                              Job model: <strong>{resumeJdEval.jd_role}</strong>
                              {resumeJdEval.jd_role_reliable ? " · reliable" : ""}
                              {resumeJdEval.jd_role_confidence
                                ? ` (${Math.round(resumeJdEval.jd_role_confidence * 100)}%)`
                                : ""}
                            </p>
                          ) : null}
                          {resumeJdEval.jd_role_note ? (
                            <p className="buy-server-product-note">{resumeJdEval.jd_role_note}</p>
                          ) : null}
                          {(resumeJdEval.categories?.length || 0) > 0 ? (
                            <div className="lease-results-table-wrap jd-eval-table" style={{ marginTop: "0.75rem" }}>
                              <table className="lease-results-table">
                                <thead>
                                  <tr>
                                    <th>Category</th>
                                    <th>Weight</th>
                                    <th>Score</th>
                                    <th>Points</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resumeJdEval.categories!.map((row) => (
                                    <tr key={row.id || row.name}>
                                      <td>
                                        {row.name || "—"}
                                        {row.summary ? ` — ${row.summary}` : ""}
                                      </td>
                                      <td>{row.weight_pct != null ? `${row.weight_pct}%` : "—"}</td>
                                      <td>{row.score_pct != null ? `${row.score_pct}%` : "—"}</td>
                                      <td>{row.weighted_points != null ? row.weighted_points : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                          {(resumeJdEval.strengths?.length || 0) > 0 ? (
                            <>
                              <h4>Key strengths</h4>
                              <ul>
                                {resumeJdEval.strengths!.slice(0, 5).map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                          {(resumeJdEval.achievements?.length || 0) > 0 ? (
                            <>
                              <h4>Achievements</h4>
                              <ul>
                                {resumeJdEval.achievements!.slice(0, 5).map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                          {(resumeJdEval.mandatory_status?.length || 0) > 0 ? (
                            <>
                              <h4>Mandatory requirements</h4>
                              <ul>
                                {resumeJdEval.mandatory_status!.map((item) => (
                                  <li key={item.req_id || item.text}>
                                    {item.status || "unclear"}: {item.text}
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                          {(resumeJdEval.advantages?.length || 0) > 0 ? (
                            <>
                              <h4>Advantages</h4>
                              <ul>
                                {resumeJdEval.advantages!.slice(0, 5).map((a) => (
                                  <li key={a}>{a}</li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                          {(resumeJdEval.gaps?.length || 0) > 0 ? (
                            <>
                              <h4>Gaps</h4>
                              <ul>
                                {resumeJdEval.gaps!.slice(0, 5).map((g) => (
                                  <li key={g}>{g}</li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                          {(resumeJdEval.requirements?.length || 0) > 0 ? (
                            <div className="lease-results-table-wrap">
                              <table className="lease-results-table">
                                <thead>
                                  <tr>
                                    <th>ID</th>
                                    <th>Category</th>
                                    <th>Score</th>
                                    <th>Requirement</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resumeJdEval.requirements!.map((r) => (
                                    <tr key={r.req_id || r.text}>
                                      <td>{r.req_id}</td>
                                      <td>{r.category}</td>
                                      <td>
                                        {r.score != null ? `${r.score}/5` : "—"}
                                      </td>
                                      <td>
                                        {(r.text || "").slice(0, 160)}
                                        {(r.text || "").length > 160 ? "…" : ""}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                          {(resumeJdEval.limitations?.length || 0) > 0 ? (
                            <p className="buy-server-product-note" role="status">
                              {resumeJdEval.limitations![0]}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : analyze ? (
                <form className="deposit-form buy-server-form train-step" onSubmit={onTrain}>
                  <div className="buy-server-row">
                    <label htmlFor={targetId}>Predict</label>
                    <select
                      id={targetId}
                      required
                      value={target}
                      onChange={(ev) => setTarget(ev.target.value)}
                      className="train-target-select"
                    >
                      <option value="" disabled>
                        Select what to predict…
                      </option>
                      {targetOptions.map((opt) => {
                        const value = targetLabel(opt);
                        const partner = opt.top_predictor || "";
                        const hint =
                          analyze.task_hints?.by_column?.[value] === "binary_classification"
                            ? " · binary"
                            : "";
                        return (
                          <option key={value} value={value}>
                            {targetOptionText(opt)}
                            {partner ? ` (from ${partner})` : ""}
                            {hint}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="buy-server-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={busy !== null || !target}
                    >
                      {busy === "train" ? "Building your model…" : "Build my AI model"}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="buy-server-product-note">
                  {lockedPath === "agent"
                    ? "Start in step 1: upload a Word, Excel, or PDF file."
                    : lockedPath === "train"
                      ? "Start in step 1: upload a CSV file."
                      : "Start in step 1: Make AI models (CSV) or Ask AI about a file (Word / Excel / PDF)."}
                </p>
              )}
            </div>
          )}
        </section>

        {showModelOnlySteps && (
        <section className={`train-guide-step${openStep === 3 ? " train-guide-step--open" : ""}`}>
          <button
            type="button"
            className="train-guide-tab"
            role="tab"
            aria-expanded={openStep === 3}
            aria-controls="train-guide-panel-3"
            id="train-guide-tab-3"
            onClick={() => setOpenStep(3)}
            disabled={!trained || agentMode}
          >
            <span className="train-guide-num">3.</span> Try your AI model
          </button>
          {openStep === 3 && (
            <div
              className="train-guide-panel"
              role="tabpanel"
              id="train-guide-panel-3"
              aria-labelledby="train-guide-tab-3"
            >
              {results ? (
                <div className={`train-verdict train-verdict--${tone}`}>
                  {(summary?.headline || !ready) && (
                    <h3>
                      {summary?.headline || "Not reliable enough"}
                    </h3>
                  )}
                    <p>
                      {summary?.body ||
                        (ready
                          ? "Enter your predictor values below and click Get model response."
                          : "Without high accuracy, reliability, and confidence, predictions are not useful.")}
                    </p>

                  {!ready && (
                    <p className="train-verdict-warn" role="status">
                      No API or try-form is offered until the model is production grade.
                    </p>
                  )}

                  {ready && (
                    <div className="train-api-box">
                      <form
                        className="deposit-form buy-server-form train-predict-form"
                        onSubmit={onPredict}
                      >
                        <div className="train-predict-row">
                          {features.map((f) => (
                            <div className="train-predict-field" key={f}>
                              <label htmlFor={`pred-${compact ? "c-" : ""}${f}`}>{f}</label>
                              <input
                                id={`pred-${compact ? "c-" : ""}${f}`}
                                type="number"
                                step="any"
                                required
                                value={predictInputs[f] ?? ""}
                                onChange={(ev) =>
                                  setPredictInputs((prev) => ({ ...prev, [f]: ev.target.value }))
                                }
                                placeholder="1"
                                disabled={busy !== null}
                                className="train-predict-input"
                              />
                            </div>
                          ))}
                          <button
                            type="submit"
                            className="btn btn-primary train-predict-submit"
                            disabled={busy !== null}
                          >
                            {busy === "predict" ? "Predicting…" : "Get model response"}
                          </button>
                        </div>
                      </form>

                      {predictError && (
                        <p className="model-run-error" role="alert">
                          {predictError}
                        </p>
                      )}

                      {prediction && (
                        <div className="deposit-success train-predict-result">
                          <div className="train-predict-result-line">
                            <h4 className="train-predict-result-title">Model response</h4>
                            <p className="train-predict-result-text">
                              {prediction.interpretation || (
                                <>
                                  Predicted <strong>{prediction.target_feature}</strong>:{" "}
                                  <strong>
                                    {prediction.prediction != null
                                      ? prediction.prediction.toFixed(2)
                                      : "—"}
                                  </strong>
                                </>
                              )}
                            </p>
                          </div>
                          <div className="train-predict-buy-row buy-model-access-actions">
                            <button
                              type="button"
                              className="btn btn-buy-model"
                              onClick={() => setOpenStep(4)}
                            >
                              Buy Model Access
                            </button>
                            <button
                              type="button"
                              className="btn btn-schedule-consult"
                              onClick={() => void onScheduleConsultation()}
                            >
                              Schedule a Consultation
                            </button>
                          </div>
                          {calendlyError && (
                            <p className="model-run-error" role="alert">
                              {calendlyError}
                            </p>
                          )}
                        </div>
                      )}

                    </div>
                  )}
                </div>
              ) : (
                <p className="buy-server-product-note">
                  Complete steps 1 and 2, then train to use your model here.
                </p>
              )}
            </div>
          )}
        </section>
        )}

        {showModelOnlySteps && (
        <section className={`train-guide-step${openStep === 4 ? " train-guide-step--open" : ""}`}>
          <button
            type="button"
            className="train-guide-tab"
            role="tab"
            aria-expanded={openStep === 4}
            aria-controls="train-guide-panel-4"
            id="train-guide-tab-4"
            onClick={() => setOpenStep(4)}
            disabled={!trained || agentMode}
          >
            <span className="train-guide-num">4.</span> Buy Model Access
          </button>
          {openStep === 4 && (
            <div
              className="train-guide-panel"
              role="tabpanel"
              id="train-guide-panel-4"
              aria-labelledby="train-guide-tab-4"
            >
              {trained ? (
                <form
                  className="deposit-form buy-server-form train-step"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void startModelAccessPayPal();
                  }}
                >
                  <div className="buy-package-cards" role="radiogroup" aria-label="Package">
                    {BUY_MODEL_PACKAGES.map((pkg) => {
                      const expanded = buyPackageExpanded[pkg.product] ?? false;
                      const selected = buyPackage === pkg.product;
                      return (
                        <div
                          key={pkg.product}
                          className={`buy-package-card${selected ? " buy-package-card--selected" : ""}${expanded ? " buy-package-card--expanded" : ""}`}
                          role="radio"
                          aria-checked={selected}
                          tabIndex={0}
                          onClick={() => {
                            setBuyPackage(pkg.product);
                            setBuyNote(null);
                          }}
                          onKeyDown={(ev) => {
                            if (ev.key === "Enter" || ev.key === " ") {
                              ev.preventDefault();
                              setBuyPackage(pkg.product);
                              setBuyNote(null);
                            }
                          }}
                        >
                          <div className="buy-package-card-head">
                            <div className="buy-package-card-title">{pkg.title}</div>
                            <button
                              type="button"
                              className="buy-package-card-toggle"
                              aria-expanded={expanded}
                              aria-label={`${expanded ? "Collapse" : "Expand"} ${pkg.product} details`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setBuyPackageExpanded((prev) => ({
                                  ...prev,
                                  [pkg.product]: !expanded,
                                }));
                              }}
                            >
                              <span
                                className={`buy-package-card-chevron${expanded ? " buy-package-card-chevron--up" : ""}`}
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                          {!expanded && (
                            <button
                              type="button"
                              className="linkish buy-package-see-more"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setBuyPackageExpanded((prev) => ({
                                  ...prev,
                                  [pkg.product]: true,
                                }));
                              }}
                            >
                              See details
                            </button>
                          )}
                          {expanded && (
                            <dl className="buy-package-details">
                              {pkg.details.map((row) => (
                                <div key={row.label} className="buy-package-detail-row">
                                  <dt>{row.label}</dt>
                                  <dd>{row.value}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="buy-model-contact-fields">
                    <div className="buy-model-contact-row">
                      <input
                        type="text"
                        className="train-model-input buy-model-contact-input"
                        placeholder="Full Name"
                        value={buyerName}
                        onChange={(e) => {
                          setBuyerName(e.target.value);
                          clearBuyerFieldError("buyer_name");
                        }}
                        aria-invalid={Boolean(buyerFieldErrors.buyer_name)}
                        required
                        autoComplete="name"
                      />
                      <BuyerFieldError message={buyerFieldErrors.buyer_name} />
                    </div>
                    <div className="buy-model-contact-row">
                      <input
                        type="email"
                        className="train-model-input buy-model-contact-input"
                        placeholder="Email Address"
                        value={buyerEmail}
                        onChange={(e) => {
                          setBuyerEmail(e.target.value);
                          clearBuyerFieldError("buyer_email");
                        }}
                        aria-invalid={Boolean(buyerFieldErrors.buyer_email)}
                        required
                        autoComplete="email"
                      />
                      <BuyerFieldError message={buyerFieldErrors.buyer_email} />
                    </div>
                    <div className="buy-model-contact-row">
                      <input
                        type="tel"
                        className="train-model-input buy-model-contact-input"
                        placeholder="(555) 123-4567 or +44 …"
                        value={buyerPhone}
                        onChange={(e) => {
                          setBuyerPhone(formatPhoneInput(e.target.value));
                          clearBuyerFieldError("buyer_phone");
                        }}
                        inputMode="tel"
                        aria-invalid={Boolean(buyerFieldErrors.buyer_phone)}
                        required
                        autoComplete="tel"
                      />
                      <BuyerFieldError message={buyerFieldErrors.buyer_phone} />
                    </div>
                  </div>
                  <div className="buy-server-actions buy-model-access-actions buy-model-paypal-actions">
                    <button
                      type="submit"
                      className="btn btn-paypal"
                      disabled={
                        buyPaypalLoading ||
                        busy !== null ||
                        !trained?.session_id ||
                        (!buyPaypalAmount && buyPackage !== "Enterprise")
                      }
                    >
                      {buyPaypalLoading
                        ? buyPackage === "Enterprise"
                          ? "Sending inquiry…"
                          : "Sending email & redirecting to PayPal…"
                        : buyPackage === "Enterprise"
                          ? "Request Enterprise Access"
                          : "Buy Model Access with PayPal"}
                    </button>
                  </div>
                  <p className="deposit-note buy-model-paypal-note">
                    {buyPackage === "Enterprise"
                      ? "We will email your Enterprise request (name, email, phone, package) to our team."
                      : "We email your package and contact details first, then redirect you to PayPal."}
                  </p>
                  {buyNote && (
                    <p className="buy-server-product-note" role="status">
                      Selected: <strong>{buyNote}</strong>
                    </p>
                  )}
                </form>
              ) : (
                <p className="buy-server-product-note">Train a model in step 3 first.</p>
              )}
            </div>
          )}
        </section>
        )}
      </div>

      {error && (
        <p className="model-run-error" role="alert">
          {error}
        </p>
      )}

      {sampleDataModalOpen && (
        <TrainSampleDataModal onClose={() => setSampleDataModalOpen(false)} />
      )}
    </div>
  );
}
